/**
 * UserManagementPage — `/admin/users`, the staff console for every kind of
 * person on the platform.
 *
 * PEOPLE-FIRST (ops mock version 13, §B4; Jeff, 28 October 2026). Eight tabs, in
 * this order, each owning a distinct population:
 *
 *   subscribers    — subscriber identities and their per-node memberships
 *                    (ADR-081 multi-node; ADR-098 Part B per-membership editing)
 *   staff          — Nexanomics staff accounts (invite, role, disable, purge,
 *                    password / authenticator reset, audit trail)
 *   graders        — everyone holding the Grader role (`users/GradersTab`)
 *   consultants    — the consultant programme, and Appoint a consultant
 *                    (`users/ConsultantsTab`, which was the Dashboard Access tab)
 *   advocates      — Advocate orgs' people; Detach only (`users/AdvocatesTab`)
 *   partners       — partner organisations and applications (`users/PartnersTab`)
 *   orphaned       — accounts with no administering member (ADR-112 D3)
 *   geo            — the Phase-3b geo-override admin, mounted in-page
 *
 * ⭐ "DASHBOARD ACCESS" IS NO LONGER A TAB. The person-centric access panel
 * (ADR-113 D25, `PersonAccessPanel`, inside the person's drilldown) opens from
 * the eye on any person row in any tab, and remains the only place access is
 * changed or removed. A tab that listed access by ORG beside a panel that lists
 * it by PERSON was two screens onto one fact — the fragmentation D25 ends.
 *
 * ── ADR-113 D25 (Amendment 3): THIS PAGE IS THE ENTRY POINT *AND* THE OWNER ──
 *
 * > **Nexa staff manage access from one person-centric screen.** Staff search
 * > for a human being and see every place that person has access… **This is the
 * > resolution of B53:** there is one surface that owns operator access, so
 * > nothing falls between two tools and **no screen can edit a row it does not
 * > own.**
 *
 * D25's "search for a human being" happens HERE — the staff and subscriber tabs
 * are the search — and the person themself is the **subscriber drilldown**,
 * which now carries the access sections alongside memberships, display name,
 * account status, the danger zone and the activity trail.
 *
 * ⭐⭐ IT DID NOT START THAT WAY, AND THE HISTORY IS THE WARNING. D25 was first
 * built as a SEPARATE screen at `/admin/users/:sub/access`, with an Access link
 * on every row — so a decision demanding *one* person-centric screen produced
 * TWO, which is the fragmentation it exists to end. And they were not
 * duplicates: the drilldown showed only `USER#` rows, so a consultant assigned
 * to subscriber accounts (`CASSIGN#` rows) was **invisible on the screen that
 * claimed to centralise everything about him.** That is B53, live, and merging
 * the sections in is what closed it.
 *
 *   ⛔ SO THE OLD RULE HERE IS INVERTED, AND DELIBERATELY. This page previously
 *      read "MUST NOT GROW THE ABILITY TO EDIT AN OPERATOR ATTACHMENT". It now
 *      owns exactly that — through `PersonAccessPanel`, which writes via
 *      `/admin/people/{sub}/access`. What has NOT changed is the boundary the
 *      old rule protected: the generic memberships editor below still refuses
 *      operator rows, because `subscriber-memberships.ts` 409s
 *      (`operator_membership_readonly`) on those routes. The rows moved to the
 *      surface whose API accepts them; they did not become editable *here*.
 *
 *   ⚠ THAT REFUSAL WAS LIVE AND THE CLIENT DID NOT MATCH IT. The server's
 *      predicate was widened to "NOT a subscriber tier", so a `partner_admin` on
 *      a `partner` node 409s. `isOperatorMembership` was not widened, so this
 *      editor rendered the commonest operator row with a working-looking role
 *      `<select>` and Remove — **a button that always failed**, which is how B53
 *      felt to use in the first place. `membershipRows` is the fix: the editor
 *      now takes SUBSCRIBER-tier rows only, by the same `nodeType` the server
 *      derives `kind` from, so the two sections are complements by construction.
 *
 * ⚠ `/admin/users/:sub/access` STILL RESOLVES — as a redirect back here with the
 * person's drilldown open (`PersonAccessRedirect`). A bookmarked or shared deep
 * link must not 404; that would be a regression dressed as a tidy-up.
 *
 * ── The role gate ───────────────────────────────────────────────────────────
 *
 * The page opens for `nexa_admin` and `super_admin`, mirroring the SERVER's
 * `staff:read` rather than choosing its own set. It is deliberately COARSER than
 * the operations inside it: `nexa_admin` may manage non-super staff, and the
 * backend's super-target forbid blocks them from mutating a `super_admin`. So
 * super-only operations are visible here and 403 server-side. That is intended —
 * the client gate is defence-in-depth for a direct URL load; the server gate is
 * the real one, and duplicating its finer-grained rules here would be a second
 * implementation free to drift from it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PersonAccessPanel } from './PersonAccessPanel';
import { useStaffMe } from '../../hooks/useStaffMe';
import { GeoOverridesPage } from '../staff/GeoOverridesPage';
import { ConsultantsTab } from './users/ConsultantsTab';
import { GradersTab } from './users/GradersTab';
import { AdvocatesTab } from './users/AdvocatesTab';
import { PartnersTab } from './users/PartnersTab';
import { PersonEye } from './users/shared';
import './users/people-tabs.css';
import { useToast, toastFromError } from '../../components/ToastProvider';
import { useReAuth, ReauthCancelledError } from '../../lib/reauth';
import { ActionConfirmModal } from './ActionConfirmModal';
import { FilterPills } from '../../components/FilterPills';
import { FilterSearch } from '../../components/FilterSearch';
import { ColumnTooltip, type ColumnTooltipItem } from '../../components/ColumnTooltip';
import { InstantTime, formatDate } from '../../lib/time-format';
// #21 — the ONE mapper for staff-API failures. Never render err.message directly.
import { friendlyStaffError, friendlyFromUnknown, actionErrorToast } from '../../lib/friendly-error';
import {
  listStaff,
  getStaff,
  inviteStaff,
  updateStaff,
  disableStaff,
  enableStaff,
  listStaffAudit,
  purgeStaff,
  resetStaffPassword,
  resetStaffAuthenticator,
  staffRoleLabel,
  STAFF_ROLES,
  STAFF_ROLE_LABELS,
  STAFF_ROLE_DESCRIPTIONS,
  STAFF_AUDIT_ACTION_LABELS,
  type StaffRole,
  type StaffStatus,
  type StaffListItem,
  type StaffDetail,
  type StaffAuditEntry,
  type StaffSignInEntry,
} from '../../lib/staff-users-api';
import {
  listAllSubscribers,
  listAllSubscriberNodes,
  createSubscriber,
  getSubscriberDetail,
  updateSubscriber,
  disableSubscriber,
  enableSubscriber,
  resendSubscriberInvite,
  deleteSubscriber,
  addMembership,
  updateMembership,
  removeMembership,
  subscriberRoleLabel,
  subscriberAuditActionLabel,
  displayRole,
  nodeBadgeLabel,
  SUBSCRIBER_ROLES,
  SUBSCRIBER_ROLE_LABELS,
  SUBSCRIBER_ROLE_DESCRIPTIONS,
  ROLES_BY_TIER,
  isOperatorMembership,
  isOperatorOrgNode,
  listAllOrphanedAccounts,
  locateSubscriber,
  type OrphanedAccount,
  type OrphanSweepIntegrity,
  type SubscriberNode,
  type SubscriberRole,
  type SubscriberListItem,
  type SubscriberDetail,
  type SubscriberAccountRow,
  type SubscriberAuditEntry,
} from '../../lib/subscribers-api';
import {
  IS_MULTI_ZONE, ONE_ZONE_REASON, ZONES, consoleZoneLabel, useWorkingZone, zoneByCode, type PortalZone,
} from '../../lib/zones';
import {
  useZonePassthroughReport, useZoneReadReport, useZoneWriteReport,
} from '../../components/ZoneContext';
import {
  ZoneCell, ZoneFilter, ZoneFanoutWarning, matchesZone,
} from '../../components/ZoneIndicators';
import { SupportPasswordModal } from './SupportPasswordModal';
import { ActionIcon } from '../../components/ActionIcon';

/**
 * /admin/users — Staff User Management (Phase 5d-3a, ADR-039).
 *
 * Manual lifecycle of nexa-admin operator accounts on the Partners pattern:
 * list → detail → action (invite / change role / disable / re-enable) with
 * confirm-before-mutate modals, toast-on-action, and an inline audit trail.
 *
 * super_admin-only — the COG item is already gated to super_admin; this guard
 * is defence-in-depth for a direct URL load. All mutations are also gated
 * server-side (ADR-012), so the surface never relies on the client gate alone.
 */
export function UserManagementPage() {
  const me = useStaffMe();

  if (me.status === 'loading') {
    return (
      <main className="portal-page">
        <p className="portal-muted">Loading…</p>
      </main>
    );
  }
  // UAT fix: User Management is account-admin work — staff:read is {nexa_admin, super_admin}, and
  // nexa_admin manages NON-super staff (the backend super-target forbid blocks nexa_admin from
  // mutating super_admin accounts). The page opens for both; super-only operations 403 server-side.
  if (me.status === 'error' || (me.me.role !== 'super_admin' && me.me.role !== 'nexa_admin')) {
    return (
      <main className="portal-page">
        <h1>User Management</h1>
        <p className="portal-error" role="alert">
          User Management is restricted to Nexa Admin and Super Admin roles.
        </p>
      </main>
    );
  }

  return <UserManagementSurface currentStaffId={me.me.staffId} />;
}

// ---------------------------------------------------------------------------

function StaffRoleBadge({ role }: { role: string }) {
  return <span className={`portal-badge portal-users-role-${role}`}>{staffRoleLabel(role)}</span>;
}

const STAFF_STATUS_LABELS: Record<StaffStatus, string> = {
  active: 'Active',
  disabled: 'Disabled',
  invited: 'Invited',
};

/**
 * Resolve the badge to SHOW from the DDB staffStatus plus the live Cognito
 * enrichment (#7, 23 Jun 2026). The DDB status only knows active/invited/
 * disabled — it does NOT reflect a password reset (which sets
 * FORCE_CHANGE_PASSWORD on an already-active account) or an authenticator reset
 * (which clears TOTP enrolment). Both previously left the row reading "Active".
 *
 * Precedence (most-specific lifecycle state wins):
 *   1. disabled / invited — the DDB status is authoritative (an invited account
 *      is also FORCE_CHANGE_PASSWORD, but "Invited" is the right label for it).
 *   2. FORCE_CHANGE_PASSWORD on an otherwise-active account → password reset
 *      pending first sign-in.
 *   3. mfaPending → authenticator reset; re-enrol forced next sign-in.
 *   4. otherwise the plain staffStatus (Active).
 */
export function staffDisplayStatus(item: {
  staffStatus: StaffStatus;
  cognitoStatus?: string;
  mfaPending?: boolean;
}): { key: string; label: string } {
  if (item.staffStatus === 'disabled') return { key: 'disabled', label: 'Disabled' };
  if (item.staffStatus === 'invited') return { key: 'invited', label: 'Invited' };
  if (item.cognitoStatus === 'FORCE_CHANGE_PASSWORD') {
    return { key: 'pending-password', label: 'Pending sign-in' };
  }
  if (item.mfaPending) return { key: 'pending-mfa', label: 'MFA re-enrol' };
  return {
    key: item.staffStatus,
    label: STAFF_STATUS_LABELS[item.staffStatus] ?? item.staffStatus,
  };
}

function StaffStatusBadge({
  status,
  cognitoStatus,
  mfaPending,
}: {
  status: StaffStatus;
  cognitoStatus?: string;
  mfaPending?: boolean;
}) {
  const { key, label } = staffDisplayStatus({ staffStatus: status, cognitoStatus, mfaPending });
  return <span className={`portal-badge portal-users-status-${key}`}>{label}</span>;
}

/**
 * Column-tooltip content (5d-3b UAT Fix 2). Plain-language explanations of the
 * Role and Status column values, surfaced via the `?` help circle on each
 * header. The status set is shared by the Staff and Subscribers tabs.
 */
const STAFF_ROLE_TOOLTIP: ColumnTooltipItem[] = [
  { term: 'Super Admin', desc: 'Full platform access including security controls.' },
  { term: 'Nexa Admin', desc: 'Full operational access; cannot manage staff accounts.' },
  { term: 'Support', desc: 'Read + limited write (subscription reactivation only).' },
  { term: 'Auditor', desc: 'Read-only access to all views; no mutations.' },
];

const SUBSCRIBER_ROLE_TOOLTIP: ColumnTooltipItem[] = [
  { term: 'Entity Admin', desc: 'Full access to their entity account, including billing (ADR-088).' },
  { term: 'Group Admin', desc: 'Manages all entities within their group, including group billing.' },
  { term: 'Group Staff', desc: 'Operational access across entities in their group.' },
  { term: 'Partner Admin', desc: 'Full access across their partner network.' },
  { term: 'Partner Staff', desc: 'Operational access across their partner network.' },
  { term: 'Partner Viewer', desc: 'Read-only access across their partner network.' },
];

const ACCOUNT_STATUS_TOOLTIP: ColumnTooltipItem[] = [
  { term: 'Active', desc: 'Account is enabled and can sign in.' },
  { term: 'Invited', desc: 'Invitation email sent; account not yet activated.' },
  { term: 'Disabled', desc: 'Account suspended; cannot sign in.' },
  { term: 'Pending sign-in', desc: 'Password was reset; the user must set a new password on next sign-in.' },
  { term: 'MFA re-enrol', desc: 'Authenticator was reset; the user must re-enrol their authenticator on next sign-in.' },
];

type StatusFilter = 'all' | StaffStatus;

/**
 * The tabs, in the order Jeff and UAT chose (ops mock version 13, §B4; 28 October 2026), each with
 * the one-line description the page header shows while it is open.
 */
export const USER_TABS = [
  { key: 'subscribers', label: 'Subscribers', description: "Subscriber accounts and the people on them; the eye opens a person's access." },
  { key: 'staff', label: 'Staff', description: 'Manage Nexanomics Admin staff accounts — invite, change role, and disable or re-enable.' },
  { key: 'graders', label: 'Graders', description: "Everyone holding the Grader role — pause and resume here; removal with a reason is on a person's access. Inviting graders arrives with the paid-grader pool." },
  { key: 'consultants', label: 'Consultants', description: "Consultants in the programme — appoint here; assign to subscriber accounts from a person's row." },
  { key: 'advocates', label: 'Advocates', description: "Advocates join through the Advocate Onboarding Wizard; staff never attach, only detach. Payouts wait on Nexanomics' Stripe activation." },
  { key: 'partners', label: 'Partners', description: "Partner organisations — applications and approvals, the partner invite; a partner's contact from their row." },
  { key: 'orphaned', label: 'Orphaned Accounts', description: 'Accounts with no administering member.' },
  { key: 'geo', label: 'Geo Restrictions', description: 'Geo overrides — who may sign in from where.' },
] as const;
export type UserTab = (typeof USER_TABS)[number]['key'];
type SortKey = 'name' | 'role' | 'status' | 'created';
type SortDir = 'asc' | 'desc';
/** Role display order for sorting (most → least privileged). */
const ROLE_RANK: Record<string, number> = {
  super_admin: 0,
  nexa_admin: 1,
  support: 2,
  read_only: 3,
  audit_only: 4,
};

function UserManagementSurface({ currentStaffId }: { currentStaffId: string }) {
  const [items, setItems] = useState<StaffListItem[]>([]);
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [loadError, setLoadError] = useState<string>('');

  /**
   * ⭐ THE TAB CAN BE ADDRESSED BY URL, because `/admin/users/:sub/access`
   * redirects here (ADR-113 Am.3) and has to land on the tab whose drilldown
   * owns operator attachments. Read ONCE as the initial value rather than kept
   * in sync with the URL: a staff member who then clicks another tab must not
   * have the redirect's choice reassert itself under them.
   */
  const [params, setParams] = useSearchParams();
  const [tab, setTabState] = useState<UserTab>(() => {
    const asked = params.get('tab');
    return USER_TABS.some((t) => t.key === asked) ? (asked as UserTab) : 'subscribers';
  });
  /**
   * The person drilldown for the people tabs that do not own one (Graders, Consultants,
   * Advocates, Partners). It rides the SAME `?open=<sub>&zone=<code>` address the Subscribers
   * tab uses, so a link to a person is one shape wherever it was copied from. A change made
   * in the drawer (a removal, a pause) remounts the tab on close so its list is fresh.
   */
  const openSub = params.get('open');
  const openZone = zoneByCode(params.get('zone') ?? undefined);
  const [tabEpoch, setTabEpoch] = useState(0);
  const drawerChanged = useRef(false);
  const setPersonLink = (sub: string | null, zoneCode?: string) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (sub && zoneCode) { next.set('open', sub); next.set('zone', zoneCode); } else { next.delete('open'); next.delete('zone'); }
      return next;
    });
  const setTab = (t: UserTab) => {
    // A drawer belongs to the tab it was opened from — switching tabs closes it.
    if (openSub) setPersonLink(null);
    setTabState(t);
  };
  const ownsDrawer = tab === 'graders' || tab === 'consultants' || tab === 'advocates' || tab === 'partners';
  const [search, setSearch] = useState('');
  // Role filter as a multi-select Set — drives the shared FilterPills.
  const [roleFilter, setRoleFilter] = useState<Set<StaffRole>>(new Set(STAFF_ROLES));
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [uatFilter, setUatFilter] = useState<'all' | 'only' | 'hide'>('all');
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'name', dir: 'asc' });

  const [selectedSub, setSelectedSub] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadState('loading');
    try {
      const { staff } = await listStaff();
      setItems(staff);
      setLoadState('loaded');
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
      setLoadState('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const roleCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of STAFF_ROLES) c[r] = 0;
    items.forEach((s) => { if (c[s.role] !== undefined) c[s.role]++; });
    return c;
  }, [items]);

  const rolePillGroups = useMemo(
    () => STAFF_ROLES.map((r) => ({ key: r, label: STAFF_ROLE_LABELS[r], count: roleCounts[r] ?? 0 })),
    [roleCounts],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = items.filter((s: StaffListItem) => {
      if (!roleFilter.has(s.role as StaffRole)) return false;
      if (statusFilter !== 'all' && s.staffStatus !== statusFilter) return false;
      if (uatFilter === 'only' && !s.uatTest) return false;
      if (uatFilter === 'hide' && s.uatTest) return false;
      if (q) {
        const hay = `${s.email} ${s.displayName ?? ''} ${s.staffId}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const factor = sort.dir === 'asc' ? 1 : -1;
    const val = (s: StaffListItem): string | number => {
      switch (sort.key) {
        case 'name': return (s.displayName || s.email).toLowerCase();
        case 'role': return ROLE_RANK[s.role] ?? 9;
        case 'status': return s.staffStatus;
        case 'created': return s.createdAt ?? '';
      }
    };
    return [...list].sort((a, b) => {
      const av = val(a); const bv = val(b);
      return av < bv ? -1 * factor : av > bv ? 1 * factor : 0;
    });
  }, [items, search, roleFilter, statusFilter, uatFilter, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  const sortArrow = (key: SortKey) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  return (
    <main className="portal-page portal-users-page nx-users">
      <header className="portal-applications-header">
        <div>
          <h1>User Management</h1>
          <p className="portal-muted" data-testid="users-tab-description">
            {USER_TABS.find((x) => x.key === tab)!.description}
          </p>
        </div>
      </header>

      <nav className="portal-partners-tabs" aria-label="User Management sections">
        {USER_TABS.map((x) => (
          <button
            key={x.key}
            type="button"
            className={`portal-partners-tab${tab === x.key ? ' active' : ''}`}
            aria-current={tab === x.key ? 'page' : undefined}
            onClick={() => setTab(x.key)}
          >
            {x.label}
          </button>
        ))}
      </nav>

      {/* The Subscribers tab reads its own `?open=<sub>&zone=<code>` deep link (ADR-113 Am.3;
          ADR-123 Stage 3b): `/admin/users/:sub/access` redirects here so a bookmarked
          person-access URL still opens that person, in whichever zone holds them. */}
      {tab === 'subscribers' && <SubscribersTab />}
      {tab === 'graders' && <GradersTab key={tabEpoch} onOpenPerson={setPersonLink} />}
      {tab === 'consultants' && <ConsultantsTab key={tabEpoch} onOpenPerson={setPersonLink} />}
      {tab === 'advocates' && <AdvocatesTab key={tabEpoch} onOpenPerson={setPersonLink} />}
      {tab === 'partners' && <PartnersTab key={tabEpoch} onOpenPerson={setPersonLink} />}
      {tab === 'orphaned' && <OrphanedAccountsTab />}
      {tab === 'geo' && <GeoOverridesPage embedded />}

      {ownsDrawer && openSub && openZone && (
        <SubscriberDetailDrawer
          cognitoSub={openSub}
          zone={openZone}
          onClose={() => {
            setPersonLink(null);
            if (drawerChanged.current) { drawerChanged.current = false; setTabEpoch((n) => n + 1); }
          }}
          onChanged={() => { drawerChanged.current = true; }}
        />
      )}

      {tab === 'staff' && (
        <>
          {/* Add panel sits UNDER the tab strip, inside the Staff tab — same
              vertical order as the Subscribers tab (tab strip → add panel →
              list). The Subscribers tab does this via AddAccountUserPanel inside
              SubscribersTab (ADR-072 §9 fork-per-surface). */}
          <AddStaffPanel onAdded={() => void load()} />

          <RoleLegend />

          <div className="portal-applications-controls">
            <FilterSearch
              value={search}
              onChange={setSearch}
              placeholder="Search name or email…"
              ariaLabel="Search staff by name or email"
            />
            <FilterPills
              groups={rolePillGroups}
              selected={roleFilter}
              onChange={setRoleFilter}
              ariaLabel="Filter staff by role"
            />
          </div>

          <div className="portal-users-filters">
            <label className="portal-users-filter-label">
              Status
              <select
                className="portal-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="invited">Invited</option>
                <option value="disabled">Disabled</option>
              </select>
            </label>
            <label className="portal-users-filter-label">
              Test Accounts
              <select
                className="portal-select"
                value={uatFilter}
                onChange={(e) => setUatFilter(e.target.value as 'all' | 'only' | 'hide')}
              >
                <option value="all">Include</option>
                <option value="only">Only</option>
                <option value="hide">Hide</option>
              </select>
            </label>
          </div>

      {loadState === 'loading' && <p className="portal-muted">Loading staff…</p>}
      {loadState === 'error' && (
        <div className="portal-error" role="alert">
          Could not load staff. {loadError}{' '}
          <button type="button" className="portal-link-button" onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}

      {loadState === 'loaded' && (
        <table className="portal-users-table">
          <thead>
            <tr>
              <th scope="col">
                <button type="button" className="portal-users-sort-btn" onClick={() => toggleSort('name')}>
                  Staff member{sortArrow('name')}
                </button>
              </th>
              <th scope="col">
                <span className="portal-col-help-wrap">
                  <button type="button" className="portal-users-sort-btn" onClick={() => toggleSort('role')}>
                    Role{sortArrow('role')}
                  </button>
                  <ColumnTooltip columnName="Role" items={STAFF_ROLE_TOOLTIP} />
                </span>
              </th>
              <th scope="col">
                <span className="portal-col-help-wrap">
                  <button type="button" className="portal-users-sort-btn" onClick={() => toggleSort('status')}>
                    Status{sortArrow('status')}
                  </button>
                  <ColumnTooltip columnName="Status" items={ACCOUNT_STATUS_TOOLTIP} />
                </span>
              </th>
              <th scope="col">
                <button type="button" className="portal-users-sort-btn" onClick={() => toggleSort('created')}>
                  Created{sortArrow('created')}
                </button>
              </th>
              <th scope="col">
                <span className="portal-visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="portal-users-empty">
                  No staff match the current filters.
                </td>
              </tr>
            )}
            {filtered.map((s) => (
              <tr key={s.cognitoSub}>
                <td>
                  <div className="portal-users-name">{s.displayName || s.email}</div>
                  {s.displayName && <div className="portal-users-sub-email">{s.email}</div>}
                  <div className="portal-users-staffid">
                    {s.staffId}
                    {s.uatTest && <span className="portal-badge portal-users-uat-badge">UAT</span>}
                  </div>
                </td>
                <td>
                  <StaffRoleBadge role={s.role} />
                </td>
                <td>
                  <StaffStatusBadge
                    status={s.staffStatus}
                    cognitoStatus={s.cognitoStatus}
                    mfaPending={s.mfaPending}
                  />
                </td>
                <td className="portal-users-created">
                  {s.createdAt ? formatDate(s.createdAt) : '—'}
                </td>
                <td className="portal-users-row-actions portal-actions-col">
                  <div className="portal-actions-row">
                    <PersonEye name={s.displayName || s.email} onOpen={() => setSelectedSub(s.cognitoSub)} />
                  </div>
                  {/* ⛔ THE "Access" LINK IS GONE (ADR-113 Am.3). It opened a
                      SECOND person-centric screen; the access sections now live
                      in the drilldown that View opens, which is the one screen
                      D25 asks for. Two buttons onto one person is the
                      fragmentation the decision exists to end.

                      ⚠ AND ON THIS TAB IT WAS NEVER MEANINGFUL: a staff row's
                      `cognitoSub` is a STAFF-pool sub, while operator attachments
                      hang off SUBSCRIBER-pool identities — so the page it opened
                      could only ever have been empty for a staff member. */}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

        </>
      )}

      {selectedSub && (
        <StaffDetailDrawer
          cognitoSub={selectedSub}
          currentStaffId={currentStaffId}
          onClose={() => setSelectedSub(null)}
          onChanged={() => void load()}
        />
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------

function RoleLegend() {
  return (
    <details className="portal-users-legend">
      <summary>Role reference</summary>
      <ul>
        {STAFF_ROLES.map((r) => (
          <li key={r}>
            <StaffRoleBadge role={r} /> <span>{STAFF_ROLE_DESCRIPTIONS[r]}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}

// ---------------------------------------------------------------------------

function SubscriberRoleBadge({ role }: { role: string }) {
  return (
    <span className={`portal-badge portal-users-role-${role}`}>{subscriberRoleLabel(role)}</span>
  );
}

/** Role-filter bucket — a known Cedar role, else the catch-all 'legacy'. */
function roleBucket(role: string): string {
  return (SUBSCRIBER_ROLES as readonly string[]).includes(role) ? role : 'legacy';
}

/**
 * Orphaned Accounts tab (ADR-112 D3/D4) — customer accounts with no member who
 * can administer them.
 *
 * WHY THIS EXISTS AT ALL. Before ADR-112 this state was unreachable: the
 * last-admin guard refused to let an account get there. That sounds protective
 * and was not — the same refusal blocked every legitimate cleanup, which is why
 * ADR-079 records that staff had no way to delete a customer account. Removing
 * the guard makes the state reachable, so it must be visible. The sentinel row
 * keeps such an account administrable; this view is how anyone finds out.
 *
 * READ-ONLY BY DESIGN. It adds no mutation surface: re-admin runs through Dashboard
 * Access / the team routes, deletion through the ADR-079 cascade. The sweep only
 * finds.
 */
function OrphanedAccountsTab() {
  const toast = useToast();
  const [rows, setRows] = useState<OrphanedAccount[]>([]);
  const [integrity, setIntegrity] = useState<OrphanSweepIntegrity | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [failedZones, setFailedZones] = useState<string[]>([]);
  const [zoneFilter, setZoneFilter] = useState('all');

  const load = useCallback(async () => {
    setLoadState('loading');
    try {
      // ⭐ AN ORPHAN IS A DATA-INTEGRITY FINDING, so a zone missing from this sweep does not
      // merely shorten a list — it reports the platform as healthier than it is.
      const res = await listAllOrphanedAccounts();
      setRows(res.orphaned);
      setIntegrity(res.integrity);
      setFailedZones(res.failedZones);
      setLoadState('loaded');
    } catch (err) {
      // Errors surface as a friendly toast, never a raw backend string on the
      // page (the shared error-surfacing standard).
      setLoadState('error');
      toast.push({ variant: 'error', ...toastFromError(err, 'Could not load orphaned accounts') });
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  // ADR-123 Decision 1 — the navigation bar names the zones this sweep covered and its counts.
  useZoneReadReport({ rows: loadState === 'loaded' ? rows : null, failedZones, filter: zoneFilter });

  // The zone filter narrows the TABLE. The integrity line below deliberately keeps reporting
  // the whole sweep: it answers "what did this scan cover", which a filter must not rewrite.
  const visibleRows = rows.filter((r) => matchesZone(r, zoneFilter));

  return (
    <section className="portal-orphans">
      <p className="portal-orphans-intro">
        Customer accounts with <strong>no administering member</strong>. Each still holds its
        permanent anchor row, so it remains administrable — re-admin it from{' '}
        a person&apos;s <strong>access panel</strong> (the eye on their row) or the account&apos;s team, or delete it via the account
        delete flow. Operator and management orgs are excluded: they are legitimately empty.
        Accounts still being set up are excluded too.
      </p>

      {/* ADR-122 Pattern 1 — an orphan sweep that silently covered one jurisdiction would
          report the platform as healthier than it is, so the zones that failed are named and
          the zone each orphan is in is shown. */}
      <ZoneFanoutWarning
        failedZones={failedZones}
        noun="orphaned accounts"
        onRetry={() => void load()}
      />
      {/* ADR-123 Decision 2 — always rendered, even before the sweep returns or when it is
          empty; disabled with the reason in a one-zone bundle. */}
      <div className="portal-users-filters">
        <ZoneFilter value={zoneFilter} onChange={setZoneFilter} />
      </div>

      {/* Skeleton and status text work in tandem — the skeleton conveys the
          structure that is coming, the text conveys what is happening. */}
      {loadState === 'loading' && (
        <div className="portal-orphans-loading" aria-busy="true">
          <p className="portal-orphans-status" role="status">Loading orphaned accounts…</p>
          <div className="portal-orphans-skeleton" aria-hidden="true">
            <span /><span /><span />
          </div>
        </div>
      )}

      {loadState === 'error' && (
        <p className="portal-orphans-status" role="status">
          Could not load orphaned accounts.{' '}
          <button type="button" className="portal-link-button" onClick={() => void load()}>
            Retry
          </button>
        </p>
      )}

      {loadState === 'loaded' && rows.length === 0 && (
        <p className="portal-orphans-status" role="status">
          No orphaned accounts — every customer account has at least one administering member.
        </p>
      )}

      {loadState === 'loaded' && rows.length > 0 && (
        <table className="portal-table portal-orphans-table">
          <thead>
            <tr>
              <th>Business</th>
              <th>Zone</th>
              <th>Customer #</th>
              <th>Email</th>
              <th>Created</th>
              <th>Account ID</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r) => (
              <tr key={r.accountId}>
                <td>
                  {r.businessName || '—'}
                  {/* An account missing its anchor is MORE degraded, not less —
                      flagged rather than hidden from the list. */}
                  {!r.hasSentinel && (
                    <span className="portal-badge portal-orphans-unanchored">UNANCHORED</span>
                  )}
                </td>
                <td><ZoneCell zone={r.zone} /></td>
                <td>{r.customerNumber || '—'}</td>
                <td>{r.email || '—'}</td>
                <td>{r.createdAt ? formatDate(r.createdAt) : '—'}</td>
                <td className="portal-orphans-id">{r.accountId}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {loadState === 'loaded' && integrity && (
        <p className="portal-orphans-integrity">
          Scanned {integrity.accountsScanned} accounts · {integrity.operatorOrgsSkipped} operator
          org(s) excluded · {integrity.inFlightExcluded} still in setup
          {integrity.missingSentinel.length > 0 && (
            <> · <strong>{integrity.missingSentinel.length} missing their anchor row</strong> — run
            the ADR-112 sentinel backfill</>
          )}
        </p>
      )}
    </section>
  );
}

/**
 * Subscribers tab (ADR-072) — full Account-User lifecycle, mirroring the Staff
 * pattern: an "Add new Account User" panel (create + Cedar role + node), a
 * Cedar-role-aware list, and a View → SubscriberDetailDrawer (rename / role-change
 * / disable / enable / delete / resend / reset password). Legacy owner/member/
 * reseller rows display gracefully (a 'Legacy' filter bucket); new/edited rows
 * always write a Cedar role.
 */
function SubscribersTab() {
  const [items, setItems] = useState<SubscriberListItem[]>([]);
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [loadError, setLoadError] = useState('');
  // ADR-122 Pattern 1 — the fan-out's partial-read outcome, and the narrowing control.
  const [failedZones, setFailedZones] = useState<string[]>([]);
  const [zoneFilter, setZoneFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<Set<string>>(new Set([...SUBSCRIBER_ROLES, 'legacy']));
  // BOW-finesse B2 — Beta filter (orthogonal to role). Default 'all' so beta
  // users are visible by default; 'only' / 'hide' narrow the list.
  const [betaFilter, setBetaFilter] = useState<'all' | 'only' | 'hide'>('all');
  // ADR-061 — the subscriber whose password is being reset (drives the modal).
  const [resetTarget, setResetTarget] = useState<SubscriberListItem | null>(null);
  // ADR-072 — the subscriber whose detail drawer is open.
  /**
   * ⭐⭐ THE OPEN DRAWER LIVES IN THE URL — `?open=<sub>&zone=<code>` (ADR-123 Stage 3b).
   *
   * `open=` used to name the person but not the zone, so a bookmarked or shared link to a US
   * subscriber asked the home zone, found nothing, and showed an EMPTY drawer — "they don't
   * exist", the exact class ADR-122 Pattern 2 closed for detail pages. Now View writes both
   * into the address bar (so the link an operator copies is complete), the drawer asks the named
   * zone, and a link with NO zone (an old bookmark, `/admin/users/:sub/access`'s redirect) is
   * resolved by `SubscriberZoneLocator` asking every zone — never by guessing the home zone.
   *
   * Opening and closing edit the URL rather than local state, so closing genuinely closes (the
   * parameter is removed) and nothing re-derives it back open.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const viewSub = searchParams.get('open');
  const viewZone = searchParams.get('zone');
  const setDeepLink = (sub: string | null, zone?: string | null) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (sub) next.set('open', sub); else next.delete('open');
      if (sub && zone) next.set('zone', zone); else next.delete('zone');
      return next;
    }, { replace: !sub });
  // PL9 — nodeId → business name, resolved from the node-attachment list so the
  // table row shows the Entity/Group/Partner NAME rather than a bare number/ID.
  // Best-effort: a load failure leaves the map empty and the row falls back to
  // the customer number.
  const [nodeLabels, setNodeLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    // Every zone's nodes, or a US row's account name falls back to its customer number while
    // an AU row shows a label — which reads as missing data rather than as an unasked zone.
    void listAllSubscriberNodes()
      .then(({ nodes }) => {
        const m: Record<string, string> = {};
        for (const n of nodes) m[n.nodeId] = n.label;
        setNodeLabels(m);
      })
      .catch(() => { /* best-effort — row falls back to the customer number */ });
  }, []);

  const load = useCallback(async () => {
    setLoadState('loading');
    try {
      const res = await listAllSubscribers();
      setItems(res.subscribers);
      setFailedZones(res.failedZones);
      setLoadState('loaded');
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
      setLoadState('error');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // ADR-123 Decision 1 — the navigation bar names the zones in this list and their counts.
  useZoneReadReport({ rows: loadState === 'loaded' ? items : null, failedZones, filter: zoneFilter });

  const roleCounts = useMemo(() => {
    const c: Record<string, number> = { legacy: 0 };
    for (const r of SUBSCRIBER_ROLES) c[r] = 0;
    items.forEach((s) => { c[roleBucket(displayRole(s))]++; });
    return c;
  }, [items]);

  const rolePillGroups = useMemo(
    () => [
      ...SUBSCRIBER_ROLES.map((r) => ({ key: r, label: SUBSCRIBER_ROLE_LABELS[r], count: roleCounts[r] ?? 0 })),
      { key: 'legacy', label: 'Legacy', count: roleCounts.legacy ?? 0 },
    ],
    [roleCounts],
  );

  const betaCount = useMemo(() => items.filter((s) => s.isBeta).length, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((s) => {
      if (!roleFilter.has(roleBucket(displayRole(s)))) return false;
      if (!matchesZone(s, zoneFilter)) return false;
      if (betaFilter === 'only' && !s.isBeta) return false;
      if (betaFilter === 'hide' && s.isBeta) return false;
      if (q) {
        const hay = `${s.email} ${s.subscriberId} ${s.customerNumber}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, roleFilter, betaFilter, zoneFilter]);

  return (
    <>
      {/* `reportsZone` is off while a person's drawer is open over the form: the drawer is what
          the operator is acting on, and "Editing ap-southeast-2" (this form's default node) beside
          "Record from us-east-1" read as if the drawer's edits land in Australia. */}
      <AddAccountUserPanel onAdded={() => void load()} reportsZone={!viewSub} />

      <details className="portal-users-legend">
        <summary>Role reference</summary>
        <ul>
          {SUBSCRIBER_ROLES.map((r) => (
            <li key={r}>
              <SubscriberRoleBadge role={r} /> <span>{SUBSCRIBER_ROLE_DESCRIPTIONS[r]}</span>
            </li>
          ))}
        </ul>
      </details>

      <div className="portal-applications-controls">
        <FilterSearch
          value={search}
          onChange={setSearch}
          placeholder="Search email or CN…"
          ariaLabel="Search subscribers by email or customer number"
        />
        <FilterPills
          groups={rolePillGroups}
          selected={roleFilter}
          onChange={setRoleFilter}
          ariaLabel="Filter subscribers by role"
        />
      </div>

      {/* BOW-finesse B2 — Beta filter. Beta is a trial kind, not a Cedar role,
          so it gets its own filter alongside the role pills. */}
      <div className="portal-users-filters">
        <label className="portal-users-filter-label">
          Beta
          <select
            className="portal-select"
            value={betaFilter}
            onChange={(e) => setBetaFilter(e.target.value as 'all' | 'only' | 'hide')}
          >
            <option value="all">Include{betaCount > 0 ? ` (${betaCount})` : ''}</option>
            <option value="only">Beta only</option>
            <option value="hide">Hide beta</option>
          </select>
        </label>
        <ZoneFilter value={zoneFilter} onChange={setZoneFilter} />
      </div>

      {/* ⚠ THIS TAB FANS OUT; THE STAFF AND GEO-RESTRICTIONS TABS ON THIS SAME PAGE DO NOT,
          and that difference is correct rather than an inconsistency. Subscribers reads
          AccountUser/Account, which are per-zone. Those two read the global Staff table, which
          ADR-120 Amendment 2 makes platform-wide and the 21 September 2026 review proved
          absent from us-east-1 by DescribeTable — so "zone doesn't apply" is the permanent
          answer there, and fanning them out would invent a staff list that does not exist. */}
      <ZoneFanoutWarning
        failedZones={failedZones}
        noun="subscribers"
        onRetry={() => void load()}
      />

      {loadState === 'loading' && <p className="portal-muted">Loading subscribers…</p>}
      {loadState === 'error' && (
        <div className="portal-error" role="alert">
          Could not load subscribers. {loadError}{' '}
          <button type="button" className="portal-link-button" onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}

      {loadState === 'loaded' && (
        <table className="portal-users-table">
          <thead>
            <tr>
              <th scope="col">Subscriber</th>
              <th scope="col">Zone</th>
              <th scope="col">
                <span className="portal-col-help-wrap">
                  Role
                  <ColumnTooltip columnName="Role" items={SUBSCRIBER_ROLE_TOOLTIP} />
                </span>
              </th>
              {/* BOW-finesse B2 — dedicated Beta column (orthogonal to Role). */}
              <th scope="col">Beta</th>
              <th scope="col">
                <span className="portal-col-help-wrap">
                  Status
                  <ColumnTooltip columnName="Status" items={ACCOUNT_STATUS_TOOLTIP} />
                </span>
              </th>
              <th scope="col">Created</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="portal-users-empty">
                  {roleFilter.size === 0
                    ? 'No role pills selected — click a role pill to show subscribers.'
                    : 'No subscribers match the current filters.'}
                </td>
              </tr>
            )}
            {filtered.map((s) => (
              <tr key={s.cognitoSub || s.email}>
                <td>
                  <div className="portal-users-name">{s.email}</div>
                  {/* The USER's identifier is subscriberId (NX-SUB-…). The
                      customer number is the entity/account id and belongs to the
                      Customers table — showing it here made a subscriber and
                      their Cybercraft entity display the SAME id (UAT item #8).
                      subscriberId is now primary; CN stays only as labelled
                      secondary entity context. */}
                  <div className="portal-users-staffid">
                    {s.subscriberId || '—'}
                    {!s.enabled && <span className="portal-badge portal-users-status-disabled">Disabled</span>}
                  </div>
                  {/* PL9 (iter-2) — the entity/group NAME, resolved SERVER-side
                      (s.nodeName), is primary; the raw nodeId is the tooltip
                      (ID secondary). Falls back to the client node-list join, then
                      the customer number. PL12 — a "+N more" hint when the user
                      belongs to more than one account (full list in the drawer). */}
                  {(s.nodeName || (s.nodeId && nodeLabels[s.nodeId])) ? (
                    <div className="portal-users-sub-email" title={s.nodeId || undefined}>
                      {s.nodeName || nodeLabels[s.nodeId!]}
                      {(s.memberships?.length ?? 0) > 1 && (
                        <span className="portal-users-membership-more">
                          {' '}+{s.memberships!.length - 1} more
                        </span>
                      )}
                    </div>
                  ) : s.customerNumber ? (
                    <div className="portal-users-sub-email">Entity {s.customerNumber}</div>
                  ) : null}
                </td>
                <td><ZoneCell zone={s.zone} /></td>
                <td><SubscriberRoleBadge role={displayRole(s)} /></td>
                {/* BOW-finesse B2 — BETA badge (reuses the beta-participants
                    table chip). Empty for non-beta users. Display-only. */}
                <td>{s.isBeta && <span className="portal-beta-chip beta-chip-beta">BETA</span>}</td>
                <td>
                  <span className={`portal-badge portal-users-status-${s.enabled ? 'active' : 'disabled'}`}>
                    {s.status || (s.enabled ? 'Active' : 'Disabled')}
                  </span>
                </td>
                <td className="portal-users-created">
                  {s.createdAt ? formatDate(s.createdAt) : '—'}
                </td>
                <td className="portal-users-row-actions">
                  <span className="portal-actions-row">
                    {/* Always a zone on the link: an untagged row (fetched before zone tagging) is the home zone's,
                        exactly as `zoneByCode` routes its calls. */}
                    <ActionIcon act="view" icon="view" label="View" tip="View this person — access, memberships and history"
                      onClick={() => setDeepLink(s.cognitoSub, zoneByCode(s.zone)!.code)}
                      disabled={!s.cognitoSub} />
                    <ActionIcon act="review" icon="key" label="Reset password"
                      tip={!s.enabled ? 'Re-enable the account before resetting its password' : 'Reset password — issue a one-time support password'}
                      onClick={() => setResetTarget(s)}
                      disabled={!s.cognitoSub || !s.enabled} />
                  </span>
                  {/* ⛔ THE "Access" LINK IS GONE (ADR-113 Am.3) — the access
                      sections moved INTO the drilldown that View opens, so this
                      row now has one way in instead of two. D25 asks for one
                      person-centric screen; a second button onto the same person
                      is the fragmentation it exists to end.

                      The `/admin/users/:sub/access` ROUTE still resolves — it
                      redirects here with this person's drilldown open — so a
                      bookmarked deep link keeps working. */}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {resetTarget && (
        <SupportPasswordModal
          subscriber={{
            cognitoSub: resetTarget.cognitoSub,
            email: resetTarget.email,
            // The subscriber's OWN zone — a reset aimed at the wrong one 404s on a person the
            // operator is looking at, and support is on the phone when that happens.
            zone: zoneByCode(resetTarget.zone),
          }}
          onClose={() => {
            setResetTarget(null);
            void load(); // status may now be FORCE_CHANGE_PASSWORD — refresh
          }}
        />
      )}

      {viewSub && !viewZone && (
        <SubscriberZoneLocator
          cognitoSub={viewSub}
          onFound={(code) => setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('zone', code);
            return next;
          }, { replace: true })}
          onClose={() => setDeepLink(null)}
        />
      )}
      {viewSub && viewZone && (
        <SubscriberDetailDrawer
          cognitoSub={viewSub}
          zone={zoneByCode(viewZone)!}
          onClose={() => setDeepLink(null)}
          onChanged={() => void load()}
        />
      )}
    </>
  );
}

/**
 * ADR-123 Stage 3b — resolves a `?open=<sub>` deep link that carries no zone.
 *
 * Asks every zone. Found in exactly one → hands that zone back and the drawer opens there.
 * Found in none → a clear not-found state naming the zones asked — NEVER an empty drawer, which
 * is how "they don't exist" used to be said about a US person. Found in more than one (a
 * person's sub is per-zone, so this should not happen) → the operator chooses, rather than the
 * console guessing. A zone that did not answer is named, so "not found" is never claimed about a
 * zone that was not actually asked.
 */
function SubscriberZoneLocator({
  cognitoSub,
  onFound,
  onClose,
}: {
  cognitoSub: string;
  onFound: (zoneCode: string) => void;
  onClose: () => void;
}) {
  const [state, setState] = useState<
    | { status: 'locating' }
    | { status: 'done'; found: PortalZone[]; failedZones: string[] }
  >({ status: 'locating' });

  useEffect(() => {
    let alive = true;
    setState({ status: 'locating' });
    void locateSubscriber(cognitoSub).then((r) => {
      if (!alive) return;
      if (r.found.length === 1 && r.failedZones.length === 0) {
        onFound(r.found[0]!.code);
        return;
      }
      setState({ status: 'done', ...r });
    });
    return () => { alive = false; };
    // `onFound` is a fresh closure each render; the lookup is keyed on the person only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cognitoSub]);

  if (state.status === 'locating') {
    return (
      <p className="portal-muted" role="status">
        Finding this person across every zone…
      </p>
    );
  }
  const asked = ZONES.filter((z) => !state.failedZones.includes(z.label)).map((z) => z.label);
  return (
    <div className="portal-error" role="alert" data-testid="subscriber-locator">
      {state.found.length === 0 ? (
        <>
          No subscriber matching this link was found in {asked.join(' or ') || 'any zone'}.
        </>
      ) : (
        <>
          This person was found in more than one zone — choose which to open:{' '}
          {state.found.map((z) => (
            <button key={z.code} type="button" className="portal-link-button" onClick={() => onFound(z.code)}>
              Open in {z.label}
            </button>
          ))}
        </>
      )}
      {state.failedZones.length > 0 && (
        <> {state.failedZones.join(' and ')} did not respond, so the person may be there.{' '}
          {state.found.map((z) => (
            <button key={`f-${z.code}`} type="button" className="portal-link-button" onClick={() => onFound(z.code)}>
              Open in {z.label}
            </button>
          ))}
        </>
      )}{' '}
      <button type="button" className="portal-link-button" onClick={onClose}>Close</button>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * AddAccountUserPanel (ADR-072 D1/D2, item 3) — fork of AddStaffPanel for the
 * Subscribers tab (§9 fork-per-surface). Adds the node-attachment picker and a
 * role dropdown TIER-FILTERED by the selected node, so an invalid (role, node)
 * pair is unrepresentable. Degrades to an Entity picker when no Group/Partner
 * nodes exist.
 */
function AddAccountUserPanel({
  onAdded,
  reportsZone = true,
}: {
  onAdded: () => void;
  /** False while a drawer sits over this form, so the Zone bar describes the drawer only. */
  reportsZone?: boolean;
}) {
  const toast = useToast();
  const reauth = useReAuth();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  /**
   * ADR-122 Track 7 — the zone this new subscriber will be created IN.
   *
   * ⭐⭐ THE NODE PICKER IS THE ZONE CHOICE; THIS CONTROL ONLY NARROWS IT. A new subscriber is
   * attached to a node, and a node lives in exactly one zone — so once a node is picked, the
   * zone is decided and cannot be independently overridden. Offering a free-standing "create
   * in zone" dropdown ALONGSIDE the node picker would let an operator select a US zone and an
   * AU node, which is not a create-in-the-wrong-place bug so much as an unsatisfiable request:
   * the US Lambda would 404 on a node it does not hold.
   *
   * So this is a FILTER over the node list, and the create call follows the chosen node's own
   * zone. Before this, `listSubscriberNodes()` returned only the home zone's nodes, so no
   * amount of zone selection could have expressed "create against a US account" — which is why
   * the test plan lists getting a test account into us-east-1 as a workaround rather than a
   * capability.
   */
  const [createZone, setCreateZone] = useState('all');
  const [nodes, setNodes] = useState<SubscriberNode[]>([]);
  const [nodesState, setNodesState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [nodeKey, setNodeKey] = useState<string>(''); // `${nodeType}:${nodeId}`
  const [role, setRole] = useState<SubscriberRole>('entity_admin');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    void listAllSubscriberNodes()
      .then(({ nodes: n }) => {
        if (!alive) return;
        setNodes(n);
        setNodesState('loaded');
        // ⚠ SEEDED FROM THE FILTERED LIST. Seeding from `n[0]` could preselect an
        // operator org — the form would open already pointed at a target it must
        // not offer, and a fast submit would use it without the picker ever being
        // touched.
        const selectable = n.filter((x) => !isOperatorOrgNode(x));
        if (selectable.length > 0) setNodeKey(`${selectable[0].nodeType}:${selectable[0].nodeId}`);
      })
      .catch(() => alive && setNodesState('error'));
    return () => { alive = false; };
  }, []);

  /**
   * ⭐⭐ THE OPERATOR ORGS ARE NOT ATTACHMENT TARGETS HERE EITHER.
   *
   * `membershipTargets` in the Add-Membership drawer below already applies this
   * exact filter, for reasons written out there in full. It was applied to ONE of
   * the two consumers: this CREATE form kept the raw list, so "Add New Account
   * User" still offered CONSULTANT · Nexa Consultants, BETA · Beta Programme and
   * PARTNER · cybercraft as places to attach a brand-new identity.
   *
   * ⛔ AND THIS ONE WAS THE DANGEROUS HALF. Add-Membership is refused server-side
   * (`operator_org_not_addable`); `createSubscriber` had NO such guard, so the
   * create path actually wrote the row. On a partner org node that is not even
   * inert: `loadPartnerMemberships`'s legacy-roster branch still honours a
   * `partner_admin` row there, so this generic staff form could mint real Partner
   * Dashboard access outside Dashboard Access and outside the D-A4 model. The server
   * guard is added in the same change; this filter stops offering the path.
   *
   * ⚠ FILTERED AT THE PICKER, NOT AT THE API. `/admin/subscriber-nodes` still
   * returns every node, and must: the drawer resolves an EXISTING membership's
   * node to a label from this same list, and a legacy membership on an operator
   * org would otherwise render as a raw id. Removing options is a picker concern;
   * removing data would break display.
   */
  const attachTargets = useMemo(
    () => nodes.filter((n) => !isOperatorOrgNode(n) && matchesZone(n, createZone)),
    [nodes, createZone],
  );

  // A zone change can strand the current selection on a node that is no longer offered. Reseed
  // rather than leaving a stale nodeKey: the form would look valid while pointing at a target
  // the operator can no longer see, and a fast submit would use it.
  useEffect(() => {
    if (attachTargets.length === 0) { setNodeKey(''); return; }
    if (!attachTargets.some((n) => `${n.nodeType}:${n.nodeId}` === nodeKey)) {
      setNodeKey(`${attachTargets[0]!.nodeType}:${attachTargets[0]!.nodeId}`);
    }
  }, [attachTargets, nodeKey]);

  const selectedNode = useMemo(
    () => attachTargets.find((n) => `${n.nodeType}:${n.nodeId}` === nodeKey),
    [attachTargets, nodeKey],
  );
  // ADR-123 Decision 1 — a create flow: the navigation bar names the zone the new subscriber
  // will be born in, which is the chosen node's zone (untagged ⇒ home, as `zoneByCode` routes it).
  useZoneWriteReport(reportsZone && selectedNode ? zoneByCode(selectedNode.zone)!.code : null);
  const assignableRoles = selectedNode ? ROLES_BY_TIER[selectedNode.nodeType] : [];

  // Keep the role valid for the selected node's tier (Decision 2).
  useEffect(() => {
    if (selectedNode && !ROLES_BY_TIER[selectedNode.nodeType].includes(role)) {
      setRole(ROLES_BY_TIER[selectedNode.nodeType][0]);
    }
  }, [selectedNode, role]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit = emailValid && !!selectedNode && assignableRoles.includes(role);

  const submit = async () => {
    if (!selectedNode) return;
    setBusy(true);
    try {
      const res = await reauth.withReauth(() =>
        createSubscriber({
          email: email.trim(),
          displayName: name.trim() || undefined,
          nodeType: selectedNode.nodeType,
          nodeId: selectedNode.nodeId,
          role,
        // ADR-122 Track 7 — the create lands in the NODE'S zone. Server-side,
        // `recordIdentityZone(newEmail)` writes THIS_ZONE unconditionally (there is no zone
        // argument to pass it), so choosing which zone's Lambda answers IS the whole
        // mechanism by which the new identity's home zone is set.
        }, zoneByCode(selectedNode.zone)),
      );
      toast.push({
        variant: 'success',
        title: `Created ${res.email}`,
        detail: `Role: ${subscriberRoleLabel(role)} on ${selectedNode.label}${
          selectedNode.zone ? ` in ${consoleZoneLabel(selectedNode.zone)}` : ''
        }. An invitation email has been sent.`,
      });
      setEmail('');
      setName('');
      onAdded();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      // A toast naming the action, the person, the zone, the reason and a reference — never the
      // raw error, and never inline (PR #533 finding: this used to append `err.message` — status,
      // path and body — to an inline alert).
      toast.push({
        variant: 'error',
        ...actionErrorToast(err, {
          action: 'Create Account User',
          subject: `${email.trim()} on ${selectedNode.label}`,
          zoneLabel: consoleZoneLabel(zoneByCode(selectedNode.zone)!.code),
        }),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="portal-uat-field portal-staff-add" aria-label="Add new Account User">
      <div className="portal-uat-field-header">
        <h2>Add new Account User</h2>
      </div>
      <p className="portal-uat-field-help">
        Create a subscriber Account User against a hierarchy node with a Cedar role.
        They receive an invitation email with a one-time temporary password and set
        their own password on first sign-in.
      </p>

      <div className="portal-uat-field-form">
        <label className="portal-uat-field-label">
          <span>Email Address</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
            autoComplete="off"
          />
        </label>
        <label className="portal-uat-field-label">
          <span>Display Name (Optional)</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Subscriber" />
        </label>

        {/* ADR-123 Decision 2 — always rendered; disabled with the reason in a one-zone bundle. */}
        <label className="portal-uat-field-label">
          <span>Zone</span>
          <select
            value={createZone}
            onChange={(e) => setCreateZone(e.target.value)}
            disabled={!IS_MULTI_ZONE}
            title={IS_MULTI_ZONE ? undefined : ONE_ZONE_REASON}
            aria-label="Zone to create the subscriber in"
          >
            <option value="all">All zones</option>
            {ZONES.map((z) => (
              <option key={z.code} value={z.code}>{z.label}</option>
            ))}
          </select>
          {!IS_MULTI_ZONE && <span className="zone-disabled-reason">{ONE_ZONE_REASON}</span>}
          <span className="portal-muted portal-users-role-hint">
            Narrows the nodes below. The new subscriber is created in the zone of whichever
            node you attach them to — pick a zone here to see only that zone&apos;s accounts.
          </span>
        </label>

        <label className="portal-uat-field-label">
          <span>Attach to (Entity / Group / Partner)</span>
          {nodesState === 'loading' && <span className="portal-muted">Loading nodes…</span>}
          {nodesState === 'error' && <span className="portal-error">Could not load nodes.</span>}
          {nodesState === 'loaded' && attachTargets.length === 0 && (
            <span className="portal-muted">No accounts found to attach to.</span>
          )}
          {nodesState === 'loaded' && attachTargets.length > 0 && (
            <select value={nodeKey} onChange={(e) => setNodeKey(e.target.value)} aria-label="Attach to node">
              {attachTargets.map((n) => (
                <option key={`${n.nodeType}:${n.nodeId}`} value={`${n.nodeType}:${n.nodeId}`}>
                  {nodeBadgeLabel(n)} · {n.label}
                  {/* The zone is on the OPTION, not only on the filter above: with "All
                      zones" selected the list mixes jurisdictions, and a bare account name
                      does not say which one a create would land in. */}
                  {n.zone ? ` · ${consoleZoneLabel(n.zone)}` : ''}
                </option>
              ))}
            </select>
          )}
        </label>

        <label className="portal-uat-field-label">
          <span>Role</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as SubscriberRole)}
            disabled={!selectedNode}
            aria-label="Role"
          >
            {assignableRoles.map((r) => (
              <option key={r} value={r}>{SUBSCRIBER_ROLE_LABELS[r]}</option>
            ))}
          </select>
        </label>
        <p className="portal-muted portal-users-role-hint">
          {selectedNode
            ? SUBSCRIBER_ROLE_DESCRIPTIONS[role]
            : 'Pick a node — the role choices are filtered to that tier.'}
        </p>


        <div className="portal-uat-field-actions">
          <button type="button" className="portal-btn" onClick={() => void submit()} disabled={busy || !canSubmit}>
            {busy ? 'Working…' : 'Add Account User'}
          </button>
          <span className="portal-uat-field-cost">
            An invitation email is sent · the subscriber sets their password on first sign-in.
          </span>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------

/**
 * SubscriberDetailDrawer (ADR-072 D3) — fork of StaffDetailDrawer for subscriber
 * Account Users (§9 fork-per-surface). Rename / change role (tier-filtered, node
 * re-attachment) / disable / enable / delete (typed-confirm) / resend invite, with
 * a last-entity_admin guard surfaced from the server. Recent sign-ins omitted (no
 * subscriber sign-in log — ADR-072: do not fabricate).
 */
function SubscriberDetailDrawer({
  cognitoSub,
  zone,
  onClose,
  onChanged,
}: {
  cognitoSub: string;
  /** ADR-122 Pattern 1 — the subscriber's own zone. Every call in this drawer routes to it. */
  zone: PortalZone;
  onClose: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const reauth = useReAuth();
  // ADR-123 Decision 1 — Pattern 2: the navigation bar names the zone this person was read from.
  useZonePassthroughReport(zone.code);
  const [detail, setDetail] = useState<SubscriberDetail | null>(null);
  const [audit, setAudit] = useState<SubscriberAuditEntry[]>([]);
  // ⚠ "could not read the trail" is a DISTINCT state from "the trail is empty".
  const [auditUnavailable, setAuditUnavailable] = useState(false);
  // PL12 — the user's full membership list (a user may belong to 2+ accounts).
  const [accounts, setAccounts] = useState<SubscriberAccountRow[]>([]);
  const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [error, setError] = useState('');

  const [pendingName, setPendingName] = useState('');
  const [nodes, setNodes] = useState<SubscriberNode[]>([]);
  const [confirm, setConfirm] = useState<null | 'disable' | 'enable' | 'delete' | 'discard-exit'>(null);
  const [disableReason, setDisableReason] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // ADR-081 (ADR-098 Part B) — per-membership editing. At most ONE membership is
  // editable at a time; while a row is dirty, the others lock and Add disables.
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<SubscriberRole>('entity_admin');
  const [addingMembership, setAddingMembership] = useState(false);
  const [addNodeKey, setAddNodeKey] = useState('');
  const [addRole, setAddRole] = useState<SubscriberRole>('entity_admin');
  const [membershipBusy, setMembershipBusy] = useState<string | null>(null);
  const [pulseDirty, setPulseDirty] = useState(false);
  const membershipsRef = useRef<HTMLElement | null>(null);
  const membershipDirty = editingAccountId !== null || addingMembership;

  /**
   * ⭐⭐ THE MEMBERSHIPS EDITOR OWNS SUBSCRIBER-TIER ROWS ONLY. Everything else —
   * partner orgs, operator programmes — renders in the `PersonAccessPanel`
   * sections below, which is where the actions that actually work on those rows
   * live (suspend / reactivate / remove, via `/admin/people/{sub}/access`).
   *
   * ⛔ THIS SPLIT FIXES A LIVE "BUTTON THAT ALWAYS FAILS". The server's refusal
   * (`subscriber-memberships.ts` → `isOperatorAttachment`) was widened to "NOT a
   * subscriber tier", so a `partner_admin` on a `partner` node 409s
   * `operator_membership_readonly` on every edit and every delete. The client's
   * `isOperatorMembership` was NOT widened — it still tests
   * `nodeType === 'management' || role ∈ operator_*`, which a partner row fails.
   * So this editor rendered the commonest operator row with a live role `<select>`
   * and a Remove button, and every save and every remove bounced. This page's own
   * header warned against exactly that: *"it would ship a button that always
   * fails, which is how B53 felt to use in the first place."*
   *
   * ⚠ THE PREDICATE IS `nodeType`, MIRRORING THE `kind` THE SERVER ASSIGNS, so
   * the two sections are complements by construction rather than by two client
   * predicates being kept in step — which is the drift that produced the defect
   * above. `person-access.ts` maps `partner → partner`, `management → operator`,
   * anything else → `account`; this keeps exactly the `account` ones.
   * `isOperatorMembership` is retained INSIDE the kept set as the role-axis guard
   * (a malformed row carrying an `operator_*` role on an entity node still reads
   * read-only), so both axes stay covered.
   */
  const membershipRows = useMemo(
    () => accounts.filter((a) => {
      const t = a.nodeType ?? 'entity';
      return t !== 'partner' && t !== 'management';
    }),
    [accounts],
  );

  const reload = useCallback(async () => {
    setState('loading');
    try {
      const [{ subscriber, accounts: accountRows, audit: trail, auditUnavailable: trailFailed }, nodeList] = await Promise.all([
        getSubscriberDetail(cognitoSub, zone),
        // The node picker lists EVERY zone's nodes: re-attaching a subscriber is the one
        // action here that can legitimately name a node in another jurisdiction, and a picker
        // that only showed this zone's would silently make that impossible.
        listAllSubscriberNodes().then((r) => r.nodes).catch(() => [] as SubscriberNode[]),
      ]);
      setDetail(subscriber);
      setAccounts(accountRows);
      setAudit(trail);
      setAuditUnavailable(trailFailed === true);
      // ⚠ THE FULL LIST IS STORED, INCLUDING OPERATOR ORGS — the ADD PICKER is
      // what filters (`membershipTargets`), not this. `nodes` also resolves the
      // header's node NAME (`detailNode` below), and a person attached to a
      // partner org would lose their heading if operator orgs were dropped here.
      // Filter at the point of OFFER, never at the point of KNOWLEDGE.
      setNodes(nodeList);
      setPendingName(subscriber.displayName ?? '');
      setEditingAccountId(null);
      setAddingMembership(false);
      setState('loaded');
    } catch (err) {
      // #21 — the inline load-error state is user-visible too: humanise BEFORE storing, so a
      // raw status/path/body can never be rendered by the {error} sites below.
      setError(friendlyFromUnknown(err));
      setState('error');
    }
  }, [cognitoSub, zone]);

  useEffect(() => { void reload(); }, [reload]);

  // #21 — delegates to the shared mapper (lib/friendly-error.ts). Both drawers used to carry
  // their own near-identical copy, and BOTH ended `return msg` — the leak this fixes.
  const humaniseError = (msg: string, ctx?: { nodeLabel?: string }): string =>
    friendlyStaffError(msg, ctx);

  // ADR-098 Part B — the footer Save commits the DISPLAY NAME only. Role/node
  // changes are now per-membership rows (the single "Role & node" control is
  // retired), each written on its own audited call.
  const runSave = async () => {
    if (!detail || !nameChanged) return;
    setBusy(true);
    setActionError(null);
    try {
      await reauth.withReauth(() => updateSubscriber(cognitoSub, { displayName: pendingName.trim() }, zone));
      toast.push({
        variant: 'success',
        title: 'Display name saved',
        detail: `${detail.email}: name → "${pendingName.trim()}".`,
      });
      onChanged();
      await reload();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      const friendly = humaniseError(err instanceof Error ? err.message : String(err));
      setActionError(friendly);
      toast.push({ variant: 'error', title: 'Could not save changes', detail: friendly });
    } finally {
      setBusy(false);
    }
  };

  const runDisable = async () => {
    if (!detail) return;
    setBusy(true);
    setActionError(null);
    try {
      await reauth.withReauth(() => disableSubscriber(cognitoSub, disableReason.trim() || undefined, zone));
      toast.push({ variant: 'success', title: `Disabled ${detail.email}`, detail: 'Sign-in blocked; record retained.' });
      setConfirm(null);
      setDisableReason('');
      onChanged();
      await reload();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      const friendly = humaniseError(err instanceof Error ? err.message : String(err));
      setActionError(friendly);
      toast.push({ variant: 'error', title: 'Action failed', detail: friendly });
    } finally {
      setBusy(false);
    }
  };

  const runEnable = async () => {
    if (!detail) return;
    setBusy(true);
    setActionError(null);
    try {
      await reauth.withReauth(() => enableSubscriber(cognitoSub, zone));
      toast.push({ variant: 'success', title: `Re-enabled ${detail.email}`, detail: 'Sign-in restored.' });
      setConfirm(null);
      onChanged();
      await reload();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      const friendly = humaniseError(err instanceof Error ? err.message : String(err));
      setActionError(friendly);
      toast.push({ variant: 'error', title: 'Action failed', detail: friendly });
    } finally {
      setBusy(false);
    }
  };

  const runResend = async () => {
    if (!detail) return;
    setBusy(true);
    setActionError(null);
    try {
      await reauth.withReauth(() => resendSubscriberInvite(cognitoSub, zone));
      toast.push({ variant: 'success', title: `Invite resent to ${detail.email}`, detail: 'A fresh temporary password has been emailed.' });
      onChanged();
      await reload();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      const friendly = humaniseError(err instanceof Error ? err.message : String(err));
      setActionError(friendly);
      toast.push({ variant: 'error', title: 'Could not resend invite', detail: friendly });
    } finally {
      setBusy(false);
    }
  };

  const runDelete = async () => {
    if (!detail) return;
    setBusy(true);
    setActionError(null);
    try {
      await reauth.withReauth(() => deleteSubscriber(cognitoSub, deleteConfirmText.trim(), zone));
      toast.push({ variant: 'success', title: `Permanently deleted ${detail.email}`, detail: 'Cognito account and account associations removed.' });
      setConfirm(null);
      onChanged();
      onClose();
    } catch (err) {
      if (err instanceof ReauthCancelledError) { setBusy(false); return; }
      const friendly = humaniseError(err instanceof Error ? err.message : String(err));
      setActionError(friendly);
      toast.push({ variant: 'error', title: 'Action failed', detail: friendly });
      setBusy(false);
    }
  };

  // ── ADR-081 (ADR-098 Part B) — per-membership editing handlers ──────────────
  const startEditMembership = (a: SubscriberAccountRow) => {
    // ADR-112 D7 — belt to the read-only render's braces. An operator row draws
    // no editable control, so this is unreachable through the UI; it is here so a
    // future refactor that re-adds a control cannot silently re-open B1.
    if (isOperatorMembership(a)) return;
    if (membershipDirty && editingAccountId !== a.accountId) return; // one at a time
    setEditingAccountId(a.accountId);
    const known = (SUBSCRIBER_ROLES as readonly string[]).includes(a.role);
    setEditingRole(known ? (a.role as SubscriberRole) : ROLES_BY_TIER[(a.nodeType as SubscriberNode['nodeType']) ?? 'entity']?.[0] ?? 'entity_admin');
    setActionError(null);
  };
  const discardEditMembership = () => {
    setEditingAccountId(null);
    setActionError(null);
  };

  const saveMembershipRow = async (a: SubscriberAccountRow) => {
    if (editingRole === a.role) { setEditingAccountId(null); return; } // no change
    setMembershipBusy(a.accountId);
    setActionError(null);
    try {
      await reauth.withReauth(() => updateMembership(cognitoSub, a.accountId, editingRole, zone));
      toast.push({
        variant: 'success',
        title: 'Membership updated',
        detail: `${a.nodeName || a.nodeId || a.accountId}: ${subscriberRoleLabel(editingRole)}.`,
      });
      setEditingAccountId(null);
      onChanged();
      await reload();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      const friendly = humaniseError(err instanceof Error ? err.message : String(err));
      setActionError(friendly);
      toast.push({ variant: 'error', title: 'Could not update membership', detail: friendly });
    } finally {
      setMembershipBusy(null);
    }
  };

  const removeMembershipRow = async (a: SubscriberAccountRow) => {
    setMembershipBusy(a.accountId);
    setActionError(null);
    try {
      await reauth.withReauth(() => removeMembership(cognitoSub, a.accountId, zone));
      toast.push({
        variant: 'success',
        title: 'Membership removed',
        detail: `Access to ${a.nodeName || a.nodeId || a.accountId} revoked — the person and their other memberships are unchanged.`,
      });
      setEditingAccountId(null);
      onChanged();
      await reload();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      const friendly = humaniseError(err instanceof Error ? err.message : String(err));
      setActionError(friendly);
      toast.push({ variant: 'error', title: 'Could not remove membership', detail: friendly });
    } finally {
      setMembershipBusy(null);
    }
  };

  /**
   * ⛔⛔ THE ADD PICKER OFFERS ACCOUNT MEMBERSHIPS ONLY — operator orgs are NOT
   * membership targets (Jeff, 31 July 2026, overturning the note that used to
   * sit in `reload`).
   *
   * UAT found the picker offering `BETA · Beta Programme`, `PARTNER · cybercraft`
   * and `CONSULTANT · Nexa Consultants`, every one of which fails on submit with
   * *"Operator organisations are not membership targets — use Appoint, below."*
   * **The refusal is correct; the option should not have been there to select.**
   * An option whose only possible outcome is an error message is a UI that
   * teaches the user its own bug, and the control that DOES attach someone to an
   * operator org — Appoint — is a few centimetres further down the same modal.
   *
   * ⚠ THE SERVER REFUSAL STAYS. `operator_org_not_addable` (ADR-112 D7) is the
   * real guard; this filter is presentation. The API is reachable without the
   * UI, so deleting the guard because the picker no longer offers the path would
   * be a wrong-axis fix — the kind that closes a door while leaving the wall out.
   *
   * ⚠ AND THE #17 BADGING IS NOT LOST, which was the stated reason for leaving
   * them in. `operatorPortalType` badging exists so two identically-named targets
   * (the PARTNER org "Website in a Day" vs the ENTITY account of the same name)
   * are distinguishable. Removing the operator one removes the AMBIGUITY rather
   * than the information: exactly one "Website in a Day" is a membership target,
   * so there is no longer a pair to tell apart. The badge still renders on the
   * customer accounts that remain.
   */
  const membershipTargets = useMemo(() => nodes.filter((n) => !isOperatorOrgNode(n)), [nodes]);

  const addSelectedNode = membershipTargets.find((n) => `${n.nodeType}:${n.nodeId}` === addNodeKey);
  const addAssignableRoles = addSelectedNode ? ROLES_BY_TIER[addSelectedNode.nodeType] : [];
  useEffect(() => {
    if (addSelectedNode && !ROLES_BY_TIER[addSelectedNode.nodeType].includes(addRole)) {
      setAddRole(ROLES_BY_TIER[addSelectedNode.nodeType][0]);
    }
  }, [addSelectedNode, addRole]);

  const openAddMembership = () => {
    if (editingAccountId !== null) return;
    setAddingMembership(true);
    const first = membershipTargets[0];
    if (first) {
      setAddNodeKey(`${first.nodeType}:${first.nodeId}`);
      setAddRole(ROLES_BY_TIER[first.nodeType][0]);
    }
  };
  const cancelAddMembership = () => { setAddingMembership(false); setActionError(null); };

  const confirmAddMembership = async () => {
    if (!addSelectedNode) return;
    setMembershipBusy('__add__');
    setActionError(null);
    try {
      await reauth.withReauth(() =>
        addMembership(cognitoSub, {
          nodeType: addSelectedNode.nodeType,
          nodeId: addSelectedNode.nodeId,
          role: addRole,
        // The NODE'S zone, not the drawer's: the membership row lands beside the account it
        // grants access to, and the picker now lists every zone's nodes, so the two can differ.
        }, zoneByCode(addSelectedNode.zone) ?? zone),
      );
      toast.push({
        variant: 'success',
        title: 'Membership added',
        detail: `${subscriberRoleLabel(addRole)} on ${addSelectedNode.label}.`,
      });
      setAddingMembership(false);
      onChanged();
      await reload();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      const friendly = humaniseError(
        err instanceof Error ? err.message : String(err),
        { nodeLabel: addSelectedNode.label },
      );
      setActionError(friendly);
      toast.push({ variant: 'error', title: 'Could not add membership', detail: friendly });
    } finally {
      setMembershipBusy(null);
    }
  };

  // Exit with a dirty membership row scrolls to the section and pulses the red
  // outline (ADR-098 §3 — directed, not interrogated), AND offers a way out.
  //
  // The pulse ALONE was a trap (punchlist #15): `membershipDirty` is true merely
  // because an add/edit row is OPEN, so a pulse-and-return made Exit and [×] look
  // dead with no escape but a reload. Guarding the exit is right; guarding it with
  // no door is not — so the pulse now runs alongside a discard-and-close confirm.
  // Supersedes ADR-098 §3's "never opens a dialog" (Jeff, 15 July 2026).
  const handleExit = () => {
    if (membershipDirty) {
      membershipsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setPulseDirty(true);
      window.setTimeout(() => setPulseDirty(false), 900);
      setConfirm('discard-exit');
      return;
    }
    onClose();
  };

  /** Drop whatever is pending and leave — the escape hatch behind the confirm.
   *  Only ever discards UNSAVED edits: saved rows are written per-row on Save. */
  const discardAndExit = () => {
    setConfirm(null);
    setEditingAccountId(null);
    setAddingMembership(false);
    setActionError(null);
    onClose();
  };

  const nameChanged = pendingName.trim() !== (detail?.displayName ?? '');

  // PL9 (iter-2) — prefer the SERVER-resolved node name (detail.nodeName); fall
  // back to the loaded node-list join, then a generic label. The header shows the
  // NAME (primary) with the raw nodeId as muted secondary context.
  const detailNode = nodes.find(
    (n) => n.nodeType === detail?.nodeType && n.nodeId === detail?.nodeId,
  );
  const detailNodeName = detail?.nodeName || detailNode?.label || (detail?.nodeType ? `${detail.nodeType} node` : '');

  return (
    <div className="portal-modal-backdrop" role="presentation">
      <div className="portal-modal portal-users-drawer" role="dialog" aria-modal="true" aria-label="Subscriber detail">
        <button type="button" className="portal-modal-close" onClick={handleExit} aria-label="Close detail">×</button>

        {state === 'loading' && <p className="portal-muted">Loading…</p>}
        {state === 'error' && <div className="portal-error" role="alert">Could not load subscriber. {error}</div>}

        {state === 'loaded' && detail && (
          <>
            <h2>{detail.displayName || detail.email}</h2>
            <dl className="portal-users-detail-meta">
              <div><dt>Email</dt><dd>{detail.email}</dd></div>
              <div><dt>Subscriber ID</dt><dd>{detail.subscriberId || '—'}</dd></div>
              <div>
                <dt>Status</dt>
                <dd>
                  <span className={`portal-badge portal-users-status-${detail.enabled ? 'active' : 'disabled'}`}>
                    {detail.status || (detail.enabled ? 'Active' : 'Disabled')}
                  </span>
                  {detail.status === 'FORCE_CHANGE_PASSWORD' && (
                    <span className="portal-users-pending"> · pending first sign-in</span>
                  )}
                </dd>
              </div>
              <div>
                <dt>Role</dt>
                <dd>
                  <SubscriberRoleBadge role={detail.role} />
                  {detail.nodeType && (
                    <span className="portal-users-node">
                      {' · '}
                      <span className="portal-users-node-name">{detailNodeName}</span>
                      {detail.nodeId && (
                        <span className="portal-muted portal-users-node-id"> {detail.nodeId}</span>
                      )}
                    </span>
                  )}
                </dd>
              </div>
            </dl>

            {/* ADR-081 (ADR-098 Part B) — EACH membership is its own editable
                unit: per-row role change / save / discard / remove, one row at a
                time. The single "Role & node" control is retired. */}
            <section
              ref={membershipsRef}
              className={`portal-users-memberships${membershipDirty ? ' is-dirty' : ''}${pulseDirty ? ' pulse' : ''}`}
            >
              <div className="portal-users-memberships-head">
                <h3>Account memberships{membershipRows.length > 0 ? ` (${membershipRows.length})` : ''}</h3>
                {membershipDirty && (
                  <span className="portal-users-dirty-msg" role="status">
                    Unsaved role change — save or discard before leaving
                  </span>
                )}
              </div>

              {membershipRows.length === 0 && !addingMembership && (
                <p className="portal-muted">
                  {accounts.length === 0
                    ? 'This identity has no account memberships — an orphaned identity. Add one below to grant access at a node.'
                    // ⚠ NOT "no memberships". This person HAS attachments; they are
                    // all operator ones and they render in the sections below. Saying
                    // "none" here would be a confident wrong answer about access on
                    // the screen that owns revoking it.
                    : 'No subscriber-account memberships. This person’s attachments are operator ones — see the sections below.'}
                </p>
              )}

              {membershipRows.map((a) => {
                const isEditing = editingAccountId === a.accountId;
                const locked = membershipDirty && !isEditing;
                const rowBusy = membershipBusy === a.accountId;
                const tier = (a.nodeType as SubscriberNode['nodeType']) || 'entity';
                const roleOptions = ROLES_BY_TIER[tier] ?? SUBSCRIBER_ROLES.slice();
                const selectValue = isEditing ? editingRole : (a.role as SubscriberRole);
                const roleUnchanged = editingRole === a.role;

                // ── ADR-112 D7 (B1/B9) — the ROLE-AXIS guard, still load-bearing ──
                //
                // The tier-axis rows (partner / management) no longer reach this
                // loop at all — `membershipRows` routes them to the access
                // sections below. What survives here is the MALFORMED row: an
                // `operator_*` role sitting on an `entity` node (the B3 shape,
                // malformed on exactly one axis). It is kept because the drawer
                // previously offered such a row a <select> of SUBSCRIBER roles, so
                // `operator_admin` matched no option, displayed as "Entity Admin"
                // (B9), and Save wrote that fallback over the real role (B1).
                //
                // ⚠ DO NOT DELETE THIS AS "unreachable now". It is reachable by
                // exactly the rows that are wrong in the data, which are the ones
                // that most need not to be silently rewritten.
                if (isOperatorMembership(a)) {
                  return (
                    <div key={a.accountId} className="portal-users-mrow is-readonly">
                      <span className={`portal-users-ntype ntype-${a.nodeType ?? 'management'}`}>
                        {(a.nodeType ?? 'mgmt').slice(0, 3).toUpperCase()}
                      </span>
                      <div className="portal-users-mname-wrap">
                        <div className="portal-users-mname">
                          {a.nodeName || a.nodeId || a.accountId}
                          <span className="portal-badge portal-users-operator-chip">OPERATOR</span>
                        </div>
                        <div className="portal-users-mid">{a.nodeId || a.accountId}</div>
                      </div>
                      {/* The TRUE role, never the entity_admin fallback (B9). */}
                      <span className="portal-users-mrole-static">
                        {subscriberRoleLabel(a.role)}
                      </span>
                      <div className="portal-users-mact">
                        {/* ⚠ THE POINTER USED TO SAY "Managed in Portal Access".
                            That is no longer true: this modal owns operator access
                            (D25). This row is read-only because its role and its
                            node DISAGREE — a data fault, not a surface boundary —
                            so it says which, rather than sending a staff member to
                            a screen that cannot fix it either. */}
                        <span className="portal-muted portal-users-mnote">
                          Role/node mismatch — read-only
                        </span>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={a.accountId} className={`portal-users-mrow${locked ? ' locked' : ''}${isEditing ? ' editing' : ''}`}>
                    <span className={`portal-users-ntype ntype-${a.nodeType ?? 'entity'}`}>
                      {(a.nodeType ?? 'ent').slice(0, 3).toUpperCase()}
                    </span>
                    <div className="portal-users-mname-wrap">
                      <div className="portal-users-mname">
                        {a.nodeName || a.nodeId || a.accountId}
                        {a.isBeta && <span className="portal-beta-chip beta-chip-beta">BETA</span>}
                        {isEditing && <span className="portal-users-unsaved">● unsaved</span>}
                      </div>
                      <div className="portal-users-mid">{a.nodeId || a.accountId}</div>
                    </div>
                    <select
                      className="portal-select"
                      value={selectValue}
                      disabled={locked || rowBusy}
                      aria-label={`Role for ${a.nodeName || a.accountId}`}
                      onChange={(e) => {
                        const v = e.target.value as SubscriberRole;
                        if (!isEditing) startEditMembership(a);
                        setEditingRole(v);
                      }}
                    >
                      {(roleOptions as readonly SubscriberRole[]).map((r) => (
                        <option key={r} value={r}>{SUBSCRIBER_ROLE_LABELS[r]}</option>
                      ))}
                    </select>
                    <div className="portal-users-mact">
                      {isEditing ? (
                        <>
                          <button
                            type="button" className="portal-users-ico primary"
                            title="Save this membership" disabled={rowBusy || roleUnchanged}
                            onClick={() => void saveMembershipRow(a)}
                          >
                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                          </button>
                          <button
                            type="button" className="portal-users-ico" title="Discard change"
                            disabled={rowBusy} onClick={discardEditMembership}
                          >
                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><polyline points="3 3 3 8 8 8" /></svg>
                          </button>
                          <button
                            type="button" className="portal-users-ico danger" title="Remove membership"
                            disabled={rowBusy} onClick={() => void removeMembershipRow(a)}
                          >
                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                          </button>
                        </>
                      ) : locked ? (
                        <button type="button" className="portal-users-ico" disabled title="Locked — finish the row you're editing first">
                          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                        </button>
                      ) : (
                        <button
                          type="button" className="portal-users-ico danger" title="Remove membership"
                          disabled={rowBusy} onClick={() => void removeMembershipRow(a)}
                        >
                          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {addingMembership ? (
                <div className="portal-users-mrow adding">
                  <span className="portal-users-ntype ntype-new">NEW</span>
                  <select
                    className="portal-select"
                    value={addNodeKey}
                    aria-label="Node for the new membership"
                    onChange={(e) => setAddNodeKey(e.target.value)}
                  >
                    {membershipTargets.map((n) => (
                      <option key={`${n.nodeType}:${n.nodeId}`} value={`${n.nodeType}:${n.nodeId}`}>
                        {nodeBadgeLabel(n)} · {n.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="portal-select"
                    value={addRole}
                    aria-label="Role for the new membership"
                    onChange={(e) => setAddRole(e.target.value as SubscriberRole)}
                  >
                    {(addAssignableRoles as readonly SubscriberRole[]).map((r) => (
                      <option key={r} value={r}>{SUBSCRIBER_ROLE_LABELS[r]}</option>
                    ))}
                  </select>
                  <div className="portal-users-mact">
                    <button
                      type="button" className="portal-users-ico primary" title="Add this membership"
                      disabled={membershipBusy === '__add__' || !addSelectedNode}
                      onClick={() => void confirmAddMembership()}
                    >
                      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    </button>
                    <button type="button" className="portal-users-ico" title="Cancel" onClick={cancelAddMembership}>
                      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="portal-users-addrow">
                  <button
                    type="button" className="portal-button-secondary"
                    disabled={membershipDirty || membershipTargets.length === 0}
                    title={membershipDirty ? 'Finish the row you’re editing first' : undefined}
                    onClick={openAddMembership}
                  >
                    + Add membership
                  </button>
                </div>
              )}

              <p className="portal-muted portal-users-mhint">
                A role change re-attaches the user to that node; each row is written on its own,
                and one row is editable at a time. Removing a membership revokes access at that
                node only — it does not delete the person.
              </p>
            </section>

            {/* ── ADR-113 D25 (Amendment 3) — the ACCESS sections, merged in ────
                Partner organisations · Operator programmes · Consultant
                connections · Advocate organisations.

                ⭐⭐ THEY LIVE HERE BECAUSE D25 SAYS "ONE person-centric screen"
                AND THIS ONE ALREADY EXISTED. Read literally, D25 produced a
                SECOND person screen at `/admin/users/:sub/access` — the
                fragmentation the decision exists to end. Worse, the two were not
                duplicates: the drilldown showed only `USER#` rows, so a
                consultant assigned to two subscriber accounts (`CASSIGN#` rows)
                was INVISIBLE on the screen that claimed to centralise everything
                about him. That is B53, live, and the merge is what closes it.

                ⚠ ORDER IS THE ARGUMENT: identity → what they reach → what they
                hold → activity. The access sections sit after memberships (their
                own account footprint) and before Display name, so a staff member
                scanning down meets every access-granting row before any of the
                administrative controls.

                ⛔ THE MODAL GETS LONGER AND THAT IS THE CORRECT TRADE. Do not
                "solve" it by collapsing or truncating a section — a hidden
                attachment is the entire defect this arc is about. */}
            <PersonAccessPanel
              cognitoSub={cognitoSub}
              personEmail={detail.email}
              embedded
              // Re-read the drilldown so the Activity trail below picks up the
              // audit row the mutation just wrote. Actions and their record are
              // now in the SAME modal, so a change that did not appear beside its
              // own audit entry would read as unrecorded.
              onChanged={() => { void reload(); onChanged(); }}
            />

            <section className="portal-users-actions">
              <h3>Display name</h3>
              <div className="portal-users-role-row">
                <input
                  type="text"
                  className="portal-input"
                  value={pendingName}
                  onChange={(e) => setPendingName(e.target.value)}
                  placeholder="Display name"
                  aria-label="Display name"
                />
              </div>

              {detail.status === 'FORCE_CHANGE_PASSWORD' && (
                <>
                  <h3>Invitation</h3>
                  <p className="portal-muted">Pending first sign-in. Resend to issue a fresh one-time temporary password.</p>
                  <button type="button" className="portal-button-primary" disabled={busy} onClick={() => void runResend()}>
                    Resend invite email
                  </button>
                </>
              )}

              {/* ADR-098 Part B — the single "Role & node" control is RETIRED;
                  role/node is now managed per-membership in the section above. */}

              <h3>Account status</h3>
              {detail.enabled ? (
                <button type="button" className="portal-button-secondary" disabled={busy} onClick={() => setConfirm('disable')}>
                  Disable sign-in
                </button>
              ) : (
                <button type="button" className="portal-button-primary" disabled={busy} onClick={() => setConfirm('enable')}>
                  Re-enable sign-in
                </button>
              )}

              <h3 className="portal-users-danger-h">Danger zone</h3>
              <button type="button" className="portal-button-danger" disabled={busy} onClick={() => setConfirm('delete')}>
                Delete Account User
              </button>
            </section>

            {/* ⛔⛔ THE SECTION ALWAYS RENDERS NOW (UAT fail 1a, 31 July 2026).
                It used to be `{audit.length > 0 && …}`, which meant the three
                states a staff member needs to tell apart — "nothing has happened",
                "this is what happened", and "we could not find out" — collapsed
                into two, and the failure state looked exactly like the empty one.
                The actions that write these rows are in THIS modal, a few
                centimetres above; a suspend that leaves no visible trace reads as
                unrecorded. */}
            <section className="portal-users-audit">
              <h3>Activity</h3>
              {auditUnavailable ? (
                <p className="portal-error" role="alert">
                  The activity trail could not be loaded — this is not a record that
                  nothing happened. Refresh to try again.
                </p>
              ) : audit.length === 0 ? (
                <p className="portal-muted">No recorded activity for this person yet.</p>
              ) : (
                <ul>
                  {audit.map((a, i) => (
                    <li key={i}>
                      <span className="portal-users-audit-action">
                        {subscriberAuditActionLabel(a.action)}
                      </span>
                      {a.detail ? ` — ${a.detail}` : ''}{' '}
                      <span className="portal-muted"><InstantTime iso={a.at} mode="datetime" empty="" /></span>
                      {/* ⚠ SAY WHICH ENTRIES ARE RECONSTRUCTED. A derived row is
                          read off the membership's own `grantedAt`, not from the
                          audit trail — it records WHEN access began, and cannot
                          say who else looked at it or what else was tried. On an
                          accountability surface, presenting a reconstruction as a
                          recorded event claims a precision it does not have. */}
                      {a.derived && (
                        <span className="portal-muted"> · from the membership record</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Footer: Save DISPLAY NAME (role/node is now per-membership) + an
                explicit Exit. ADR-098 §3 — Exit with a dirty membership row does
                not open a dialog; it scrolls to the section and pulses the outline. */}
            <div className="portal-modal-actions">
              <button
                type="button"
                className="portal-link-button-secondary"
                onClick={handleExit}
                disabled={busy}
              >
                Exit
              </button>
              <button
                type="button"
                className="portal-button-primary"
                onClick={() => void runSave()}
                disabled={busy || !nameChanged}
              >
                {busy ? 'Working…' : 'Save display name'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Confirms (ADR-098 Part B retired the role/node re-attachment confirm —
          role changes are now per-membership, each written directly). */}

      {/* #15 — the escape hatch. Without this, an open add/edit row makes Exit and
          [×] unresponsive with no way out. */}
      <ActionConfirmModal
        open={confirm === 'discard-exit'}
        title="Discard unsaved membership changes?"
        intent="warning"
        primaryLabel="Discard and close"
        onConfirm={discardAndExit}
        onCancel={() => setConfirm(null)}
      >
        <p>
          The membership row you’re editing hasn’t been saved. Closing now discards
          that change — memberships you already saved are unaffected.
        </p>
      </ActionConfirmModal>

      <ActionConfirmModal
        open={confirm === 'disable'}
        title={`Disable ${detail?.email ?? ''}?`}
        intent="warning"
        primaryLabel="Disable"
        loading={busy}
        error={actionError}
        onConfirm={() => void runDisable()}
        onCancel={() => { if (!busy) { setConfirm(null); setActionError(null); } }}
      >
        <p>Sign-in will be blocked; the account and its data are retained and can be re-enabled.</p>
        <label className="portal-uat-field-label">
          <span>Reason (optional)</span>
          <input value={disableReason} onChange={(e) => setDisableReason(e.target.value)} placeholder="Why?" />
        </label>
      </ActionConfirmModal>

      <ActionConfirmModal
        open={confirm === 'enable'}
        title={`Re-enable ${detail?.email ?? ''}?`}
        intent="primary"
        primaryLabel="Re-enable"
        loading={busy}
        error={actionError}
        onConfirm={() => void runEnable()}
        onCancel={() => { if (!busy) { setConfirm(null); setActionError(null); } }}
      >
        <p>Sign-in will be restored for this subscriber.</p>
      </ActionConfirmModal>

      <ActionConfirmModal
        open={confirm === 'delete'}
        title={`Delete ${detail?.email ?? ''}?`}
        intent="danger"
        primaryLabel="Permanently delete"
        primaryDisabled={deleteConfirmText.trim().toLowerCase() !== (detail?.email ?? '').toLowerCase()}
        loading={busy}
        error={actionError}
        onConfirm={() => void runDelete()}
        onCancel={() => { if (!busy) { setConfirm(null); setDeleteConfirmText(''); setActionError(null); } }}
      >
        <p>
          This permanently removes the Cognito account and every account association. This cannot
          be undone. Type the email <strong>{detail?.email}</strong> to confirm.
        </p>
        <label className="portal-uat-field-label">
          <span>Confirm email</span>
          <input value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} placeholder={detail?.email} />
        </label>
      </ActionConfirmModal>
    </div>
  );
}

// ---------------------------------------------------------------------------

function AddStaffPanel({ onAdded }: { onAdded: () => void }) {
  const toast = useToast();
  const reauth = useReAuth();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<StaffRole>('support');
  const [uatTest, setUatTest] = useState(false);
  // ADR-124 A1 — the new member's default zone, chosen by the admin creating them; it starts on
  // the admin's own working zone. Required: the member's first sign-in states it (A3).
  const workingZone = useWorkingZone();
  const [defaultZone, setDefaultZone] = useState<string>(workingZone.code);
  const [createdPassword, setCreatedPassword] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const submit = async () => {
    setBusy(true);
    try {
      // ADR-063: inviting a staff member is a mutating action — gate on a
      // fresh session (proactive + server-401 re-auth/retry) via withReauth.
      const res = await reauth.withReauth(() =>
        inviteStaff({ email: email.trim(), role, name: name.trim() || undefined, uatTest, preferredZone: defaultZone }),
      );
      if (res.uatTest && res.uatTestPassword) {
        // Test mode: reveal the known password inline (no email); keep the
        // email shown so the operator can pair them.
        setCreatedPassword(res.uatTestPassword);
        toast.push({
          variant: 'success',
          title: `Test account created: ${res.email}`,
          detail: `Role: ${staffRoleLabel(role)}. No email sent.`,
        });
        onAdded();
      } else if (res.emailSent === false) {
        // Account exists but the invite email did NOT go out (e.g. SES sandbox
        // rejects unverified recipients). Surface it — the invitee can't sign
        // in without their temp password. Re-invite resends.
        toast.push({
          variant: 'error',
          title: `Account created, but the invite email did NOT send`,
          detail: `${res.email}: ${res.emailError ?? 'send failed'}. In SES sandbox only verified recipients (e.g. @nexanomics.com) receive mail. Re-invite to retry once the address is deliverable.`,
        });
        setEmail('');
        setName('');
        onAdded();
      } else {
        toast.push({
          variant: 'success',
          title: res.resent ? `Re-invited ${res.email}` : `Invited ${res.email}`,
          detail: `Role: ${staffRoleLabel(role)}. An invitation email has been sent.`,
        });
        setEmail('');
        setName('');
        onAdded();
      }
    } catch (err) {
      // Operator cancelled the re-auth modal — abort silently.
      if (err instanceof ReauthCancelledError) return;
      // CLAUDE.md § Error surfacing — a toast with a friendly message; never inline, never raw.
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('staff_already_active')) {
        toast.push({ variant: 'error', title: 'Invitation failed', detail: 'That email already belongs to an active staff member.' });
      } else if (msg.includes('invalid_zone')) {
        toast.push({ variant: 'error', title: 'Invitation failed', detail: 'Choose a default zone this platform has.' });
      } else {
        toast.push({ variant: 'error', ...toastFromError(err, 'Invitation failed') });
      }
    } finally {
      setBusy(false);
    }
  };

  // Fires one of each toast variant — same diagnostic as the Partners panel.
  const fireTestToasts = () => {
    toast.push({ variant: 'info', title: 'Test toast — info', detail: 'Background context. Auto-dismisses after 6s.' });
    setTimeout(() => toast.push({ variant: 'success', title: 'Test toast — success', detail: 'Action completed. Auto-dismisses after 4s.' }), 400);
    setTimeout(() => toast.push({ variant: 'error', title: 'Test toast — error (HTTP 503)', detail: '/admin/staff\n{"message":"Service Unavailable"}' }), 800);
  };

  return (
    <section className="portal-uat-field portal-staff-add" aria-label="Add staff member">
      <div className="portal-uat-field-header">
        <h2>Add Staff Member</h2>
      </div>
      <p className="portal-uat-field-help">
        Invite a new Nexanomics Admin operator. They receive a branded email with a
        one-time temporary password and set a new password + enrol an authenticator
        app (TOTP) on first sign-in.
      </p>

      <label className="portal-uat-mode-toggle">
        <input type="checkbox" checked={uatTest} onChange={(e) => setUatTest(e.target.checked)} disabled={busy} />
        <span>
          UAT test mode <em>(super_admin)</em> — fakes the email send, sets a known
          password, and tags the account for cleanup
        </span>
      </label>

      <div className="portal-uat-field-form">
        <label className="portal-uat-field-label">
          <span>Email Address</span>
          <input
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setCreatedPassword(null); }}
            placeholder="name@nexanomics.com"
            autoComplete="off"
          />
        </label>
        <label className="portal-uat-field-label">
          <span>Display Name (Optional)</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Operator" />
        </label>
        <label className="portal-uat-field-label">
          <span>Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value as StaffRole)}>
            {STAFF_ROLES.map((r) => (
              <option key={r} value={r}>{STAFF_ROLE_LABELS[r]}</option>
            ))}
          </select>
        </label>
        <p className="portal-muted portal-users-role-hint">{STAFF_ROLE_DESCRIPTIONS[role]}</p>
        <label className="portal-uat-field-label">
          <span>Default zone</span>
          <select
            value={defaultZone}
            required
            disabled={!IS_MULTI_ZONE}
            title={IS_MULTI_ZONE ? undefined : ONE_ZONE_REASON}
            onChange={(e) => setDefaultZone(e.target.value)}
            aria-describedby="add-staff-zone-hint"
          >
            {ZONES.map((z) => (
              <option key={z.code} value={z.code}>{z.label}</option>
            ))}
          </select>
        </label>
        <p className="portal-muted portal-users-role-hint" id="add-staff-zone-hint">
          The zone they start in at every sign-in. They can change it later on My Profile.
          {!IS_MULTI_ZONE && <> <span className="zone-disabled-reason">{ONE_ZONE_REASON}</span></>}
        </p>

        {createdPassword && (
          <div className="portal-users-uat-banner">
            <strong>Test account created.</strong> Temporary password:{' '}
            <code>{createdPassword}</code> — sign in (you’ll enrol TOTP on first
            sign-in), then purge from the detail view when done.
          </div>
        )}
        <div className="portal-uat-field-actions">
          <button type="button" className="portal-btn" onClick={() => void submit()} disabled={busy || !emailValid}>
            {busy ? 'Working…' : uatTest ? 'Add Test Account' : 'Add Staff Member'}
          </button>
          <span className="portal-uat-field-cost">
            {uatTest
              ? 'No email sent — a known password is shown after creating.'
              : 'A branded invite email is sent · TOTP enrolled on first sign-in.'}
          </span>
          <button
            type="button"
            className="portal-link-button-secondary portal-uat-toast-test"
            onClick={fireTestToasts}
            title="Fire one of each toast variant — confirms the notification UX"
          >
            🧪 Test Toasts
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------

// Inline glyphs for the two recovery actions (project convention — no icon
// dependency; Lucide-style stroke icons). Distinct shapes so the buttons read
// apart at a glance: an envelope for the emailed password reset, a phone/device
// for the authenticator reset.
const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);
const DeviceIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="7" y="2" width="10" height="20" rx="2" />
    <path d="M11 18h2" />
  </svg>
);

function StaffDetailDrawer({
  cognitoSub,
  currentStaffId,
  onClose,
  onChanged,
}: {
  cognitoSub: string;
  currentStaffId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const reauth = useReAuth();
  const [detail, setDetail] = useState<StaffDetail | null>(null);
  const [audit, setAudit] = useState<StaffAuditEntry[]>([]);
  const [signIns, setSignIns] = useState<StaffSignInEntry[]>([]);
  const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [error, setError] = useState('');

  // Pending edits.
  const [pendingRole, setPendingRole] = useState<StaffRole | null>(null);
  const [pendingName, setPendingName] = useState<string>('');
  const [confirm, setConfirm] = useState<
    null | 'role' | 'disable' | 'enable' | 'purge' | 'reset-pw' | 'reset-mfa'
  >(null);
  const [disableReason, setDisableReason] = useState('');
  const [purgeConfirmText, setPurgeConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setState('loading');
    try {
      const [{ staff }, { audit: trail, signIns: signInLog }] = await Promise.all([
        getStaff(cognitoSub),
        listStaffAudit(cognitoSub),
      ]);
      setDetail(staff);
      setAudit(trail);
      setSignIns(signInLog);
      setPendingRole(staff.role as StaffRole);
      setPendingName(staff.displayName ?? '');
      setState('loaded');
    } catch (err) {
      // #21 — the inline load-error state is user-visible too: humanise BEFORE storing, so a
      // raw status/path/body can never be rendered by the {error} sites below.
      setError(friendlyFromUnknown(err));
      setState('error');
    }
  }, [cognitoSub]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const isSelf = detail?.staffId === currentStaffId;

  // #21 — same shared mapper as the subscriber drawer above.
  const humaniseError = (msg: string): string => friendlyStaffError(msg);

  // PL7 (UAT iter-2) — parity with the subscriber drawer: ONE Save commits the
  // display-name AND/OR role change in a single PUT. Only changed fields are
  // sent, so a name-only edit never re-issues the role and vice-versa. A role
  // change still routes through the confirm dialog (its onConfirm calls this).
  const nameChanged = !!detail && pendingName.trim() !== (detail.displayName ?? '');
  const roleChanged = !!detail && pendingRole !== null && pendingRole !== detail.role;
  const hasChanges = nameChanged || roleChanged;

  const runSave = async () => {
    if (!detail) return;
    const input: { displayName?: string; role?: StaffRole } = {};
    if (nameChanged) input.displayName = pendingName.trim();
    if (roleChanged && pendingRole) input.role = pendingRole;
    if (input.displayName === undefined && input.role === undefined) return;

    setBusy(true);
    setActionError(null);
    try {
      await reauth.withReauth(() => updateStaff(cognitoSub, input));
      const parts: string[] = [];
      if (input.displayName !== undefined) parts.push(`name → "${input.displayName}"`);
      if (input.role !== undefined) parts.push(`role → ${staffRoleLabel(input.role)}`);
      toast.push({ variant: 'success', title: 'Changes saved', detail: `${detail.email}: ${parts.join('; ')}.` });
      setConfirm(null);
      onChanged();
      await reload();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      const friendly = humaniseError(err instanceof Error ? err.message : String(err));
      setActionError(friendly);
      toast.push({ variant: 'error', title: 'Could not save changes', detail: friendly });
    } finally {
      setBusy(false);
    }
  };

  // Save click: confirm first when a role change is involved (re-attachment is
  // deliberate), otherwise commit a name-only change straight away.
  const onSaveClick = () => {
    if (roleChanged) setConfirm('role');
    else void runSave();
  };

  const runDisable = async () => {
    if (!detail) return;
    setBusy(true);
    setActionError(null);
    try {
      await reauth.withReauth(() => disableStaff(cognitoSub, disableReason.trim() || undefined));
      toast.push({ variant: 'success', title: `Disabled ${detail.email}`, detail: 'Sign-in blocked; record retained.' });
      setConfirm(null);
      setDisableReason('');
      onChanged();
      await reload();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      const friendly = humaniseError(err instanceof Error ? err.message : String(err));
      setActionError(friendly);
      toast.push({ variant: 'error', title: 'Action failed', detail: friendly });
    } finally {
      setBusy(false);
    }
  };

  const runEnable = async () => {
    if (!detail) return;
    setBusy(true);
    setActionError(null);
    try {
      await reauth.withReauth(() => enableStaff(cognitoSub));
      toast.push({ variant: 'success', title: `Re-enabled ${detail.email}`, detail: 'Sign-in restored.' });
      setConfirm(null);
      onChanged();
      await reload();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      const friendly = humaniseError(err instanceof Error ? err.message : String(err));
      setActionError(friendly);
      toast.push({ variant: 'error', title: 'Action failed', detail: friendly });
    } finally {
      setBusy(false);
    }
  };

  const runResend = async () => {
    if (!detail) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await reauth.withReauth(() =>
        inviteStaff({ email: detail.email, role: detail.role as StaffRole }),
      );
      if (res.emailSent === false) {
        toast.push({
          variant: 'error',
          title: 'Invite email did NOT resend',
          detail: `${detail.email}: ${res.emailError ?? 'send failed'}.`,
        });
      } else {
        toast.push({
          variant: 'success',
          title: `Invite resent to ${detail.email}`,
          detail: 'A fresh one-time temporary password has been emailed.',
        });
      }
      onChanged();
      await reload();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      const friendly = humaniseError(err instanceof Error ? err.message : String(err));
      setActionError(friendly);
      toast.push({ variant: 'error', title: 'Could not resend invite', detail: friendly });
    } finally {
      setBusy(false);
    }
  };

  const runPurge = async () => {
    if (!detail) return;
    setBusy(true);
    setActionError(null);
    try {
      await reauth.withReauth(() => purgeStaff(cognitoSub, detail.email));
      toast.push({
        variant: 'success',
        title: `Permanently deleted ${detail.email}`,
        detail: 'Cognito account, record, and audit trail removed.',
      });
      setConfirm(null);
      onChanged();
      onClose(); // the member no longer exists — leave the drawer
    } catch (err) {
      if (err instanceof ReauthCancelledError) {
        setBusy(false);
        return;
      }
      const friendly = humaniseError(err instanceof Error ? err.message : String(err));
      setActionError(friendly);
      toast.push({ variant: 'error', title: 'Action failed', detail: friendly });
      setBusy(false);
    }
  };

  // Item #7, Action A — reset password (EMAILED, keeps authenticator).
  const runResetPassword = async () => {
    if (!detail) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await reauth.withReauth(() => resetStaffPassword(cognitoSub));
      if (res.emailSent === false) {
        toast.push({
          variant: 'error',
          title: 'Password reset, but the email did NOT send',
          detail: `${detail.email}: ${res.emailError ?? 'send failed'}. In SES sandbox only verified recipients (e.g. @nexanomics.com) receive mail. Retry once the address is deliverable.`,
        });
      } else {
        toast.push({
          variant: 'success',
          title: `Password reset for ${detail.email}`,
          detail: 'A fresh temporary password has been emailed. Their authenticator is unchanged.',
        });
      }
      setConfirm(null);
      onChanged();
      await reload();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      const friendly = humaniseError(err instanceof Error ? err.message : String(err));
      setActionError(friendly);
      toast.push({ variant: 'error', title: 'Could not reset password', detail: friendly });
    } finally {
      setBusy(false);
    }
  };

  // Item #7, Action B — reset authenticator (re-enrol on next sign-in, keeps password).
  const runResetAuthenticator = async () => {
    if (!detail) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await reauth.withReauth(() => resetStaffAuthenticator(cognitoSub));
      // ADR-074 D4 (Stage 6 toast-on-send): the reset now sends a notification
      // email. Reflect whether it actually went — a failed send is non-fatal
      // (the reset stands) but the operator must know it didn't notify the user.
      if (res.emailSent === false) {
        toast.push({
          variant: 'error',
          title: `Authenticator reset for ${detail.email}`,
          detail: `The reset succeeded, but the notification email could NOT be sent${res.emailError ? ` (${res.emailError})` : ''}. Tell them directly: they will set up a new authenticator on next sign-in.`,
        });
      } else {
        toast.push({
          variant: 'success',
          title: `Authenticator reset for ${detail.email}`,
          detail: 'A notification email has been sent. They will set up a new authenticator on next sign-in; their password is unchanged — have them delete the old entry from their authenticator app.',
        });
      }
      setConfirm(null);
      onChanged();
      await reload();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      const friendly = humaniseError(err instanceof Error ? err.message : String(err));
      setActionError(friendly);
      toast.push({ variant: 'error', title: 'Could not reset authenticator', detail: friendly });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="portal-modal-backdrop" role="presentation">
      <div className="portal-modal portal-users-drawer" role="dialog" aria-modal="true" aria-label="Staff member detail">
        <button type="button" className="portal-modal-close" onClick={onClose} aria-label="Close detail">
          ×
        </button>

        {state === 'loading' && <p className="portal-muted">Loading…</p>}
        {state === 'error' && (
          <div className="portal-error" role="alert">
            Could not load staff member. {error}
          </div>
        )}

        {state === 'loaded' && detail && (
          <>
            <h2>{detail.displayName || detail.email}</h2>
            <dl className="portal-users-detail-meta">
              <div>
                <dt>Email</dt>
                <dd>{detail.email}</dd>
              </div>
              <div>
                <dt>Staff ID</dt>
                <dd>{detail.staffId}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>
                  <StaffStatusBadge
                    status={detail.staffStatus}
                    cognitoStatus={detail.cognitoStatus}
                    mfaPending={detail.mfaPending}
                  />
                  {detail.cognitoStatus === 'FORCE_CHANGE_PASSWORD' && (
                    <span className="portal-users-pending">
                      {detail.staffStatus === 'invited'
                        ? ' · pending first sign-in'
                        : ' · password reset — awaiting new password'}
                    </span>
                  )}
                  {detail.staffStatus !== 'disabled' &&
                    detail.cognitoStatus !== 'FORCE_CHANGE_PASSWORD' &&
                    detail.mfaPending && (
                      <span className="portal-users-pending">
                        {' '}· authenticator reset — re-enrol forced on next sign-in
                      </span>
                    )}
                </dd>
              </div>
              <div>
                <dt>Role</dt>
                <dd>
                  <StaffRoleBadge role={detail.role} />
                </dd>
              </div>
            </dl>

            {isSelf && (
              <p className="portal-muted portal-users-self-note">
                This is your own account — role and status changes must be made by another Super Admin.
              </p>
            )}

            {!isSelf && (
              <section className="portal-users-actions">
                <h3>Display name</h3>
                {/* PL7 — no per-field Save; the modal footer commits name + role
                    together in one action. */}
                <div className="portal-users-role-row">
                  <input
                    type="text"
                    className="portal-input"
                    value={pendingName}
                    onChange={(e) => setPendingName(e.target.value)}
                    placeholder="Display name"
                    aria-label="Display name"
                  />
                </div>
                <p className="portal-muted">
                  Email / username is the Cognito sign-in alias and can’t be edited.
                  To replace a staffer’s identity, disable this account and invite the new address.
                </p>

                {(detail.staffStatus === 'invited' ||
                  detail.cognitoStatus === 'FORCE_CHANGE_PASSWORD') && (
                  <>
                    <h3>Invitation</h3>
                    <p className="portal-muted">
                      Pending first sign-in. Resend to issue a fresh one-time temporary
                      password (the previous one stops working).
                    </p>
                    <button
                      type="button"
                      className="portal-button-primary"
                      disabled={busy}
                      onClick={() => void runResend()}
                    >
                      Resend invite email
                    </button>
                  </>
                )}

                <h3>Change role</h3>
                {/* PL7 — the role select feeds the single footer Save (a role
                    change routes through the confirm dialog on Save). */}
                <div className="portal-users-role-row">
                  <select
                    className="portal-select"
                    value={pendingRole ?? detail.role}
                    onChange={(e) => setPendingRole(e.target.value as StaffRole)}
                    aria-label="Select new role"
                  >
                    {STAFF_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {STAFF_ROLE_LABELS[r]}
                      </option>
                    ))}
                  </select>
                </div>

                <h3>Account status</h3>
                {detail.staffStatus === 'active' ? (
                  <button
                    type="button"
                    className="portal-button-danger"
                    onClick={() => {
                      setActionError(null);
                      setConfirm('disable');
                    }}
                  >
                    Disable account
                  </button>
                ) : (
                  <button
                    type="button"
                    className="portal-button-primary"
                    onClick={() => {
                      setActionError(null);
                      setConfirm('enable');
                    }}
                  >
                    Re-enable account
                  </button>
                )}

                {/* Account recovery (item #7) — TWO distinct, best-practice
                    actions (never bundled): emailed password reset (keeps the
                    authenticator) and authenticator reset for a lost device
                    (keeps the password). Low-prominence, inside the detail modal. */}
                <h3>Password &amp; authenticator</h3>
                <p className="portal-muted">
                  Recover a locked-out staff member. Reset the password (emailed, keeps
                  authenticator), or reset the authenticator for a lost device (re-enrol on
                  next sign-in, keeps password).
                </p>
                <div className="portal-users-recovery-row">
                  <button
                    type="button"
                    className="portal-button-primary portal-users-recovery-btn"
                    disabled={busy}
                    onClick={() => { setActionError(null); setConfirm('reset-pw'); }}
                  >
                    <MailIcon />
                    Reset password
                  </button>
                  <button
                    type="button"
                    className="portal-button-primary portal-users-recovery-btn"
                    disabled={busy}
                    onClick={() => { setActionError(null); setConfirm('reset-mfa'); }}
                  >
                    <DeviceIcon />
                    Reset authenticator
                  </button>
                </div>

                <h3 className="portal-users-danger-heading">Danger zone</h3>
                <p className="portal-muted">
                  Permanently delete removes the Cognito account, the staff
                  record, and its audit trail. This cannot be undone — prefer
                  Disable for departed staff you may need to audit later.
                </p>
                <button
                  type="button"
                  className="portal-button-danger"
                  onClick={() => {
                    setActionError(null);
                    setPurgeConfirmText('');
                    setConfirm('purge');
                  }}
                >
                  Permanently delete…
                </button>
              </section>
            )}

            <section className="portal-users-audit">
              <h3>Audit trail</h3>
              {audit.length === 0 ? (
                <p className="portal-muted">No lifecycle events recorded yet.</p>
              ) : (
                <ul className="portal-users-audit-list">
                  {audit.map((a, i) => (
                    <li key={`${a.at}-${i}`}>
                      <span className="portal-users-audit-action">
                        {STAFF_AUDIT_ACTION_LABELS[a.action] ?? a.action}
                      </span>
                      {a.detail && <span className="portal-users-audit-detail"> — {a.detail}</span>}
                      <span className="portal-users-audit-meta">
                        {' '}
                        by {a.performedByStaffId || a.performedBy} ·{' '}
                        <InstantTime iso={a.at} mode="datetime" empty="" />
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="portal-users-audit">
              <h3>Recent sign-ins</h3>
              {signIns.length === 0 ? (
                <p className="portal-muted">No sign-ins recorded yet.</p>
              ) : (
                <ul className="portal-users-audit-list">
                  {signIns.slice(0, 20).map((s, i) => (
                    <li key={`${s.at}-${i}`}>
                      <span className="portal-users-audit-action">Signed in</span>
                      {s.country && <span className="portal-users-audit-detail"> · {s.country}</span>}
                      {s.newDeviceUsed && (
                        <span className="portal-users-audit-detail"> · new device</span>
                      )}
                      <span className="portal-users-audit-meta">
                        {' '}
                        <InstantTime iso={s.at} mode="datetime" empty="" />
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* PL7 — standard modal footer: ONE Save (commits name + role
                together) and an explicit Exit (in addition to the × close).
                Shown only when this staffer is editable (not self). */}
            {!isSelf && (
              <div className="portal-modal-actions">
                <button
                  type="button"
                  className="portal-link-button-secondary"
                  onClick={onClose}
                  disabled={busy}
                >
                  Exit
                </button>
                <button
                  type="button"
                  className="portal-button-primary"
                  onClick={onSaveClick}
                  disabled={busy || !hasChanges}
                >
                  {busy ? 'Working…' : 'Save changes'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirm modals — layered above the drawer. */}
      {confirm === 'role' && detail && pendingRole && (
        <ActionConfirmModal
          open
          title="Change staff role"
          intent="warning"
          primaryLabel="Change role"
          loading={busy}
          error={actionError}
          onConfirm={() => void runSave()}
          onCancel={() => setConfirm(null)}
        >
          <p>
            Change <strong>{detail.email}</strong> from{' '}
            <strong>{staffRoleLabel(detail.role)}</strong> to{' '}
            <strong>{staffRoleLabel(pendingRole)}</strong>?
          </p>
        </ActionConfirmModal>
      )}

      {confirm === 'disable' && detail && (
        <ActionConfirmModal
          open
          title="Disable staff account"
          intent="danger"
          primaryLabel="Disable account"
          loading={busy}
          error={actionError}
          onConfirm={() => void runDisable()}
          onCancel={() => setConfirm(null)}
        >
          <p>
            Disable <strong>{detail.email}</strong>? They will be unable to sign
            in. The account and its audit trail are retained and can be
            re-enabled later.
          </p>
          <label className="portal-users-field">
            Reason (optional)
            <input
              type="text"
              className="portal-input"
              value={disableReason}
              onChange={(e) => setDisableReason(e.target.value)}
              placeholder="e.g. left the team"
            />
          </label>
        </ActionConfirmModal>
      )}

      {confirm === 'enable' && detail && (
        <ActionConfirmModal
          open
          title="Re-enable staff account"
          intent="primary"
          primaryLabel="Re-enable account"
          loading={busy}
          error={actionError}
          onConfirm={() => void runEnable()}
          onCancel={() => setConfirm(null)}
        >
          <p>
            Re-enable <strong>{detail.email}</strong>? They will be able to sign
            in again with their existing credentials.
          </p>
        </ActionConfirmModal>
      )}

      {confirm === 'purge' && detail && (
        <ActionConfirmModal
          open
          title="Permanently delete staff account"
          intent="danger"
          primaryLabel="Permanently delete"
          primaryDisabled={purgeConfirmText.trim() !== detail.email}
          loading={busy}
          error={actionError}
          onConfirm={() => void runPurge()}
          onCancel={() => setConfirm(null)}
        >
          <p>
            This <strong>irreversibly</strong> deletes <strong>{detail.email}</strong>{' '}
            — the Cognito account, the staff record, and the audit trail. It
            cannot be undone.
          </p>
          <label className="portal-users-field">
            Type <code>{detail.email}</code> to confirm
            <input
              type="text"
              className="portal-input"
              value={purgeConfirmText}
              onChange={(e) => setPurgeConfirmText(e.target.value)}
              placeholder={detail.email}
              /* Type-to-confirm input of an ActionConfirmModal (role="dialog"
                 aria-modal="true") opened by an explicit operator action.
                 Focus-on-open is the dialog pattern, and this input is the
                 only thing to do in the modal. */
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
          </label>
        </ActionConfirmModal>
      )}

      {/* Account recovery confirms (item #7). Each is a low-prominence confirm
          inside the detail modal; both run super_admin + fresh-auth. */}
      {confirm === 'reset-pw' && detail && (
        <ActionConfirmModal
          open
          title="Reset password"
          intent="warning"
          primaryLabel="Email new password"
          loading={busy}
          error={actionError}
          onConfirm={() => void runResetPassword()}
          onCancel={() => { if (!busy) setConfirm(null); }}
        >
          <p>
            Email <strong>{detail.email}</strong> a fresh temporary password. They set a new
            password on first sign-in. Their authenticator (TOTP) is <strong>unchanged</strong>.
          </p>
        </ActionConfirmModal>
      )}

      {confirm === 'reset-mfa' && detail && (
        <ActionConfirmModal
          open
          title="Reset authenticator"
          intent="warning"
          primaryLabel="Reset authenticator"
          loading={busy}
          error={actionError}
          onConfirm={() => void runResetAuthenticator()}
          onCancel={() => { if (!busy) setConfirm(null); }}
        >
          <p>
            Clear the authenticator (TOTP) enrolment for <strong>{detail.email}</strong>. They
            re-enrol a new authenticator on next sign-in (QR scan, like first login). Their
            password is <strong>unchanged</strong>. Ask them to delete the old entry from their
            authenticator app.
          </p>
        </ActionConfirmModal>
      )}
    </div>
  );
}
